import {
  useState,
  FC,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { v4 } from "uuid";
import classNames from "classnames";
import Modal from "../../components/Modal";
import DropdownMenu from "../../components/DropdownMenu";
import ShipmentPiecesDetails from "./ShipmentPiecesDetails";
import AnimatedHeight from "../../components/AnimatedHeight";
import {
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import ShipmentHeader from "../../components/Shipment/ShipmentHeader";
import CreateClientModal from "../../components/Account/CreateClientModal";
import UpdateClientModal from "../../components/Account/UpdateClientModal";
import ShipmentItemTable from "../../components/Shipment/ShipmentItemTable";
import {
  AddressSearch,
  BusinessUnitSearch,
} from "../../components/LocationSearch";
import {
  formatFloat,
  formatName,
  formatPhoneNumber,
  paymentModeFormat,
} from "../../utils/format";
import {
  setShipmentCreateDraft,
  setShipmentCreateDrafts,
  ShipmentDraft,
} from "../../store/slices";
import {
  LinkText,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import {
  FormTextInput,
  FormGroupSearch,
  FormTextAreaInput,
  FormSimpleRadioGroup,
  FormSelectOptionObjects,
  FormSearch,
} from "../../components/FormFields";
import {
  getAccount,
  loaderService,
  getPreShipment,
  getAccountWhitelist,
  getShipmentItems,
  getShipmentRates,
  getBusinessUnitByTerritory,
  getBUCredit,
  getTier,
  getWeights,
  getDeliveryDistance,
  getRatesDHL,
  convertPieceToDhlPackageDto,
  convertRatesDHLResponseToItemInterface,
  addressValidation,
  getServicesFromPaymentMode,
  getServicesFromDeliveryType,
  getLockedServicesByTier,
  getAppLockedServices,
  getService,
  getShipmentEstimatedDeliveryTime,
  alertService,
  getLockBUServices,
  updateShipmentStatus,
  updateShipment,
  getLockBUPaymentModes,
  getAccountSiteBUByAccountSiteID,
  GetAccountSiteLockByAccountSiteID,
} from "../../services";
import {
  AccountSearch,
  BoxAccountSearch,
  filterDestination,
  AccountSearchFavoriteHistoric,
} from "../../components/Account/AccountSearch";
import {
  PaymentMode,
  discountItem,
  DeliveryType,
  ItemInterface,
  ShipmentService,
  AccountInterface,
  ShipmentInterface,
  LocationInterface,
  AccountAuthorization,
  DestinationInterface,
  ShipmentStatus,
  BusinessUnitInterface,
  BusinessType,
} from "../../interfaces";
import { AddressValidationResponse } from "../../interfaces/DHLInterface";
import { GoogleMap } from "@react-google-maps/api";
import LoadingIcon from "../../components/LodingIcon";
import {
  PaymentModeDto,
  RangeDeclareDto,
  TierDto,
} from "../../interfaces/Dtos";
import {
  applyInternationalRate,
  currencyExchangeText,
  useCurrencyExchanges,
  validateAccount,
} from "../../utils";
import { AutocompleteRegion } from "../../components/Form/FormMapsAutocomplete";
import { BUCreditDTO } from "../../interfaces/Dtos/BUCreditDTO";
import { ServiceDTO } from "../../interfaces/Dtos/ServiceDTO";
import moment from "moment";
import { getDetailsFromCoords } from "../../utils/getDetailsFromCoords";
import { dhlAddressStringBuilder } from "../../utils/dhlAddressStringBuilder";
import { splitAddress } from "../../utils/splitAddress";

export const minDeclaredValue = (
  weight: number,
  rangeList: RangeDeclareDto[],
  service?: ShipmentService
) => {
  let filteredRangeList = rangeList.filter((r) => r.serviceID === service);
  if (filteredRangeList.length === 0) {
    filteredRangeList = rangeList.filter((r) => r.serviceID === 0);
  }

  return (
    filteredRangeList.find((r) => r.minWeight < weight && weight <= r.maxWeight)
      ?.minDeclareValue ?? 0
  );
};

export const WhitelistOption: FC<{
  client: AccountInterface;
  location: LocationInterface;
}> = ({ client, location }) => {
  return (
    <div className="flex items-center">
      <div className="flex flex-col">
        <p className="text-sm font-semibold leading-5 truncate">
          {formatName(client.accountFullName)}
        </p>
        {client.listAccountPhone?.length === 0 ||
        (client.abreviationName !== "V-" && client.abreviationName !== "E-") ? (
          <p className="text-sm leading-5">
            {client.abreviationName}
            {client.identificationNumber}
          </p>
        ) : (
          <p className="text-sm leading-5">
            {formatPhoneNumber(client.listAccountPhone?.[0])}
          </p>
        )}
        <p className="text-sm leading-5">{location.name}</p>
      </div>
    </div>
  );
};

enum DropdownMenuOption {
  EDIT_SHIPPER,
  EDIT_CONSIGNEE,
  CLEAN,
  DELETE,
  CANCEL,
}

interface ShipmentFormProps {
  /**
   * Indicate if show shipment data
   */
  active: boolean;
  /**
   * Shipment index
   */
  index: number;
  /**
   * Shipper is editable
   */
  shipperIsEditable?: boolean;
  disablePaymentMode?: boolean;
  /**
   * Indicate if there is an error
   */
  error?: string;
  deleteable?: boolean;
  cleanable?: boolean;
  /**
   * On delete callback
   */
  onDelete: () => void;
  /**
   * On click callback
   */
  onArrowClick: () => void;
  /**
   * Setter function con international value
   */
  setInternational: Dispatch<SetStateAction<boolean>>;
}

const ShipmentForm: FC<ShipmentFormProps> = ({
  active,
  index,
  shipperIsEditable,
  disablePaymentMode,
  error,
  deleteable = false,
  cleanable = false,
  onDelete,
  onArrowClick,
  setInternational,
}) => {
  const randomId = v4();
  const dispatch = useAppDispatch();
  const exchanges = useCurrencyExchanges();
  const loading = loaderService.useIsLoading();
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID
  );

  // Constant
  const mercadoLibreUser = "mercadolibre.ve";
  // Check if error is truthy and non-empty
  const hasError = !!error && error.trim().length > 0;
  const errorLines = hasError ? error.split("\n") : [];
  // Store data
  const user = useAppSelector((state) => state.user);
  const countries = useAppSelector((state) => state.inmutable.countries);
  const businessUnits = useAppSelector(
    (state) => state.inmutable.businessUnits
  );
  const taxIdTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  );

  const shipmentDrafts = useAppSelector(
    (state) => state.shipmentCreate.shipmentDrafts
  );
  const shipmentCreateDocument = useAppSelector(
    (state) => state.shipmentCreate.document
  );
  const shipmentDraft = shipmentDrafts[index];
  const pieceCategories = useAppSelector(
    (state) => state.inmutable.pieceCategories
  );
  const rangeMinDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  );
  const allPaymentModes = useAppSelector(
    (state) => state.inmutable.paymentModes
  );
  const holidays = useAppSelector((state) => state.inmutable.holidays);

  //DHL integration
  const [loader, setLoader] = useState(false);
  const [cityOptions, setCityOptions] = useState<AddressValidationResponse>();
  const [countryName, setCountryName] = useState("");

  // Form data
  const rateKey = useRef<string>("");
  const itemsKey = useRef<string>("");
  const mapRef = useRef<google.maps.Map>();
  const [tier, setTier] = useState<TierDto>();
  const [isLoading, setIsLoading] = useState(0);
  const [cleaned, setCleaned] = useState(false);
  const [auxReference, setAuxReference] = useState("");
  const [service, setService] = useState<ServiceDTO>();
  const [buCredit, setBuCredit] = useState<BUCreditDTO>();
  const [preShipmentCode, setPreShipmentCode] = useState("");
  const [whitelistSearch, setWhitelistSearch] = useState("");
  const [openCleanModal, setOpenCleanModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [preShipmentError, setPreShipmentError] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [consigneeType, setConsigneeType] = useState("account");
  const [openDropdownMenu, setOpenDropdownMenu] = useState(false);
  const [openCreationModal, setOpenCreationModal] = useState(false);
  const [auxConsigneeType, setAuxConsigneeType] = useState("account");
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState<number>();
  //const [disablePaymentMode, setDisablePaymentMode] = useState(false); left commented in case this would work better
  const [whitelist, setWhitelist] = useState<DestinationInterface[]>([]);
  const [openPreShipmentModal, setOpenPreShipmentModal] = useState(false);
  const [openHomeDeliveryHelp, setOpenHomeDeliveryHelp] = useState(false);
  const [clientTypeSelected, setClientTypeSelected] = useState("shipper");
  const [shipmentItems, setShipmentItems] = useState<ItemInterface[]>([]);
  const [openPaymentModeModal, setOpenPaymentModeModal] = useState(false);
  const [allowedServices, setAllowedServices] = useState<ServiceDTO[]>([]);
  const [openConsigneeTypeModal, setOpenConsigneeTypeModal] = useState(false);
  const [lockBUPaymentModes, setLockBUPaymentModes] = useState<
    PaymentModeDto[]
  >([]);
  const [openInternationalDeliveryHelp, setOpenInternationalDeliveryHelp] =
    useState(false);
  const [openInternationalAddressHelp, setOpenInternationalAddressHelp] =
    useState(false);
  const [openInternationalDimentionsHelp, setOpenInternationalDimentionsHelp] =
    useState(false);
  const [paymentModeServices, setPaymentModeServices] = useState<ServiceDTO[]>(
    []
  );
  const [tierBlockedServices, setTierBlockedServices] = useState<ServiceDTO[]>(
    []
  );
  const [appBlockedServices, setAppBlockedServices] = useState<ServiceDTO[]>(
    []
  );
  const [lockedBUServices, setLockedBUServices] = useState<ServiceDTO[]>([]);
  const [deliveryTypeServices, setDeliveryTypeServices] = useState<
    ServiceDTO[]
  >([]);
  const [accountSiteBUsMap, setAccountSiteBUsMap] = useState<
    Record<number, string[]>
  >({});
  const [accountSiteHasBURestrictionsMap, setAccountSiteHasBURestrictionsMap] =
    useState<Record<number, boolean>>({});
  const [accountSiteDeliveryTypeLocksMap, setAccountSiteDeliveryTypeLocksMap] =
    useState<Record<number, number[]>>({});
  const [corporateDestinationBUs, setCorporateDestinationBUs] = useState<
    BusinessUnitInterface[]
  >([]);
  const [auxPaymentMode, setAuxPaymentMode] = useState<
    PaymentMode | number | string
  >();
  const [declaredValue, setDeclaredValue] = useState(
    shipmentDraft.declaredValue ?? ""
  );
  const [baseShipmentItems, setBaseShipmentItems] = useState<ItemInterface[]>(
    []
  );

  // Form errors
  const shipperRef = useRef<HTMLDivElement>(null);
  const piecesRef = useRef<HTMLDivElement>(null);
  const consigneeRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState({
    shipper: "",
    consignee: "",
    location: "",
    declaredValue: "",
    items: "",
    pieces: false,
  });

  const saveDraft = useCallback(
    (draft: ShipmentDraft) => {
      dispatch(setShipmentCreateDraft({ index, shipment: draft }));
    },
    [dispatch, index]
  );

  /**
   * Deterministic fingerprint of every input that genuinely changes the rate
   * quote. Built ONLY from persisted draft fields (so it is identical before
   * and after a remount) plus the stable applicationID. Time-based inputs
   * (saleDate = new Date()) and locally re-fetched/derived state (tier, the
   * service DTO, baseShipmentItems) are intentionally EXCLUDED: tier/service
   * are derived from draft fields already included here, and including them
   * would make the signature mismatch on the first render after Regresar
   * (when they reset to undefined) and defeat the guard.
   */
  const buildQuoteSignature = useCallback((): string => {
    const d = shipmentDraft;
    const r = (n?: number) => (n == null ? null : Math.round(n * 1e6) / 1e6);
    const ship = d.shipperAddress?.coordinates ?? d.buSource?.location?.coordinates;
    const cons = d.consigneeAddress?.coordinates;
    const isIntl = d.deliveryType === DeliveryType.INTERNATIONAL;
    return JSON.stringify({
      service: d.service ?? null,
      paymentMode: d.paymentMode ?? null,
      deliveryType: d.deliveryType ?? null,
      buSource: d.buSource?.code ?? null,
      buConsignee: d.buConsignee?.code ?? null,
      shipper: d.shipper?.id ?? null,
      consignee: d.consignee?.id ?? null,
      billTo: d.accountBillTo?.id ?? null,
      siteId: d.accountSiteID ?? d.accountBillTo?.accountSiteID ?? null,
      sLat: r(ship?.lat),
      sLng: r(ship?.lng),
      cLat: r(cons?.lat),
      cLng: r(cons?.lng),
      weight: d.totalChargedWeight ?? null,
      declared: d.declaredValue ? Number(d.declaredValue) : 0,
      dist: d.deliveryDistance ?? null,
      pickup: d.pickupType ?? null,
      app: applicationID ?? null,
      srm: (d.pieces ?? []).reduce((a, p) => a + p.value, 0) > 0,
      pieces: (d.pieces ?? []).map((p) => [
        p.weight,
        p.width,
        p.height,
        p.length,
        p.value,
      ]),
      optional: (d.items ?? [])
        .filter((i) => !i.mandatory && !i.couponID)
        .map((i) => i.id)
        .sort(),
      intl: isIntl
        ? {
            postal: d.dhlDestination?.postalCode ?? null,
            county: d.dhlDestination?.countyName ?? null,
            city: d.cityName ?? null,
            country: d.countryCode ?? null,
            dhl: d.dhlItem?.rate.value ?? null,
          }
        : null,
    });
  }, [shipmentDraft, applicationID]);

  /**
   * Release the promotion held by an already-saved guide by PUTting it with
   * only its base + mandatory items (dropping optional/coupon items). The
   * backend then frees any promotion attached to those optional items so the
   * upcoming rate recompute can re-evaluate it. Best-effort: on failure the
   * released flag is cleared so a later edit can retry.
   */
  const releaseShipmentPromotion = async () => {
    const baseItems = (shipmentDraft.items ?? []).filter(
      (item) => item.isItemBase || item.mandatory
    );
    try {
      const response = await updateShipment(
        {
          id: shipmentDraft.id,
          shipmentNumber: shipmentDraft.shipmentNumber,
          consigneeAddress: shipmentDraft.consigneeAddress,
          deliveryType: shipmentDraft.deliveryType,
          items: baseItems,
        },
        true,
        false,
        user.user ?? undefined,
        applicationID
      );
      if (!response || response.didError) {
        saveDraft({ promotionReleased: false });
      }
    } catch {
      saveDraft({ promotionReleased: false });
    }
  };

  const handleRefChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!shipmentDraft.isInternational) {
      saveDraft({
        consigneeAddress: !!shipmentDraft.consigneeAddress
          ? {
              ...shipmentDraft.consigneeAddress,
              reference: event.target.value,
            }
          : undefined,
        destination: event.target.value,
      });
    } else if (!shipmentDraft.internationalAddress) {
      saveDraft({
        consigneeAddress: {
          name: shipmentDraft.internationalLocation!.country!,
          address: `${shipmentDraft.internationalLocation!.country}. ${
            shipmentDraft.internationalLocation!.city
          }. ${shipmentDraft.internationalLocation!.postalCode}`,
          reference: event.target.value,
          postalCode: shipmentDraft.internationalLocation!.postalCode,
          coordinates: {
            lat: 10.4925776,
            lng: -66.9182369,
          },
        },
        destination: event.target.value,
        internationalLocation: {
          ...shipmentDraft.internationalLocation,
          address: event.target.value,
        },
      });
    } else {
      saveDraft({
        consigneeAddress: {
          name: shipmentDraft.internationalLocation!.country!,
          address: `${shipmentDraft.internationalLocation!.country}. ${
            shipmentDraft.internationalLocation!.city
          }. ${shipmentDraft.internationalLocation!.postalCode}`,
          reference: event.target.value,
          postalCode: shipmentDraft.internationalLocation!.postalCode,
          coordinates: {
            lat: 10.4925776,
            lng: -66.9182369,
          },
        },
        destination: event.target.value,
        internationalLocation: {
          ...shipmentDraft.internationalLocation,
          address: event.target.value,
        },
        internationalAddress: {
          ...shipmentDraft.internationalAddress,
          reference: event.target.value,
        },
      });
    }
  };

  const paymentModes = useMemo(() => {
    let result = allPaymentModes
      .filter(
        (mode) =>
          mode.statusID === 1 &&
          mode.paymentModeID !== PaymentMode.CREDIT &&
          mode.paymentModeID !== PaymentMode.DEST_BOXOFFICE_CREDIT &&
          ((!!buCredit &&
            shipmentDraft.shipperAuth !== AccountAuthorization.ENTERPRISE) ||
            mode.paymentModeID !== PaymentMode.BOXOFFICE_CREDIT) &&
          (!shipmentDraft.isInternational ||
            mode.paymentModeID === PaymentMode.CONTADO) &&
          !lockBUPaymentModes.some(
            (b) => b.paymentModeID === mode.paymentModeID
          )
      )
      .map((mode) => ({
        name: paymentModeFormat(mode.paymentModeID),
        value: mode.paymentModeID,
        disabled:
          mode.paymentModeID === PaymentMode.BOXOFFICE_CREDIT &&
          (buCredit?.balance ?? 0) <= 0,
        tooltip:
          mode.paymentModeID === PaymentMode.BOXOFFICE_CREDIT &&
          (buCredit?.balance ?? 0) <= 0
            ? "La tienda usó la totalidad del crédito disponible"
            : "",
      }));

    return result;

    // You only need to know the shipperAuth and if they have a boxAccount to
    // obtain the payment methods and the auth account
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shipmentDraft.shipperAuth,
    shipmentDraft.boxAccount,
    buCredit,
    shipmentDraft.service,
    shipmentDraft.paymentMode,
  ]);

  const selectedBusinessUnitIsDefault = useMemo(() => {
    const selectedBUCode = shipmentDraft.buConsignee?.code;

    if (!selectedBUCode) {
      return true;
    }

    // A store is considered default only when it comes from the global catalog
    // and is NOT loaded via a corporate/special contract — stores tied to a
    // special contract must not expose the unrestricted credit option.
    const isInDefaultCatalog = businessUnits.some(
      (bu) => bu.code === selectedBUCode
    );
    const isLoadedFromSpecialContract = corporateDestinationBUs.some(
      (bu) => bu.code === selectedBUCode
    );

    return isInDefaultCatalog && !isLoadedFromSpecialContract;
  }, [
    businessUnits,
    corporateDestinationBUs,
    shipmentDraft.buConsignee?.code,
  ]);

  const dropdownMenuOptions = useMemo(() => {
    const { shipper, consignee } = shipmentDraft;
    const options = [];

    if (
      !!shipper &&
      shipperIsEditable &&
      !shipper?.agreementID &&
      (shipper?.listAuthorizingAccount?.length ?? 0) === 0
    ) {
      options.push({
        title: "Editar Remitente",
        value: DropdownMenuOption.EDIT_SHIPPER,
      });
    }
    if (
      !!consignee &&
      !consignee?.agreementID &&
      (consignee?.listAuthorizingAccount?.length ?? 0) === 0
    ) {
      options.push({
        title: "Editar Destinatario",
        value: DropdownMenuOption.EDIT_CONSIGNEE,
      });
    }
    if ((cleanable || shipperIsEditable) && !shipmentDraft.shipmentNumber) {
      options.push({ title: "Limpiar Guía", value: DropdownMenuOption.CLEAN });
    }
    if (
      deleteable ||
      (shipmentDrafts.length === 1 && !!shipmentDraft.shipmentNumber)
    ) {
      if (!shipmentDraft.shipmentNumber) {
        options.push({
          title: "Eliminar",
          value: DropdownMenuOption.DELETE,
        });
      } else {
        options.push({
          title: "Anular",
          value: DropdownMenuOption.CANCEL,
        });
      }
    }

    return options;
  }, [shipperIsEditable, deleteable, cleanable, shipmentDraft, shipmentDrafts]);

  const paymentModesWithAuth = useMemo(() => {
    const authAccounts = (shipmentDraft.shipper?.listAuthorizingAccount ?? []).concat(shipmentDraft.accountBillTo?.listAuthorizingAccount ?? []);
    const selectedBUCode = shipmentDraft.buConsignee?.code;
    const uniqueAccountSites: AccountInterface[] = [];
    const visitedAccountSites: Set<number> = new Set<number>();

    authAccounts.forEach((account) => {
      if (
        !account.accountSiteID ||
        visitedAccountSites.has(account.accountSiteID)
      ) {
        return;
      }

      const selectedDeliveryType = Number(shipmentDraft.deliveryType);
      const lockedDeliveryTypes =
        accountSiteDeliveryTypeLocksMap[account.accountSiteID] ?? [];

      if (lockedDeliveryTypes.includes(selectedDeliveryType)) {
        return;
      }

      if (selectedBUCode) {
        const accountSiteBUCodes = accountSiteBUsMap[account.accountSiteID] ?? [];
        const hasBURestrictions =
          accountSiteHasBURestrictionsMap[account.accountSiteID] ?? false;

        // If an accountSite has no associated BUs, treat it as unrestricted for default stores only.
        if (!hasBURestrictions && !selectedBusinessUnitIsDefault) {
          return;
        }

        if (hasBURestrictions && !accountSiteBUCodes.includes(selectedBUCode)) {
          return;
        }
      }

      uniqueAccountSites.push(account);
      visitedAccountSites.add(account.accountSiteID);
    });

    uniqueAccountSites.sort((a, b) => a.accountSiteID! - b.accountSiteID!);

    const basePaymentModes = selectedBusinessUnitIsDefault ? paymentModes : [];

    const result = [...basePaymentModes, ...uniqueAccountSites].map((option) => {
      if ("value" in option) {
        return option;
      } else {
        return {
          name:
            "Crédito de " +
            (!option.accountSiteID
              ? formatName(option.accountFullName)
              : ` ${option.accountSiteName} (${option.accountSiteCode})`),
          value: option,
          disabled: false,
          tooltip: "",
        };
      }
    });
    console.log(result);
    return result;
  }, [
    paymentModes,
    accountSiteBUsMap,
    accountSiteHasBURestrictionsMap,
    accountSiteDeliveryTypeLocksMap,
    shipmentDraft.deliveryType,
    selectedBusinessUnitIsDefault,
    shipmentDraft.buConsignee?.code,
    shipmentDraft.shipper?.listAuthorizingAccount,
    shipmentDraft.accountBillTo?.listAuthorizingAccount,
  ]);

  const availableServiceTypes = useMemo(() => {
    if (consigneeType === "locker") {
      return [
        {
          name: "Casillero Nacional",
          value: ShipmentService.NATIONAL_LOCKER,
        },
      ];
    } else {
      return allowedServices.map((service) => {
        return {
          name: service.serviceShortName,
          value: service.serviceID,
        };
      });
    }
  }, [consigneeType, allowedServices]);

  const piecesRestrictions = useMemo(() => {
    if (shipmentDraft.paymentMode === PaymentMode.CREDIT) {
      return {
        maxSumWeight: shipmentDraft.accountBillTo?.listAuthorizingAccount[0]?.maxWeightAllowed,
      };
    }

    return {
      maxSumWeight: undefined,
    };
  }, [
    shipmentDraft.accountBillTo?.listAuthorizingAccount,
    shipmentDraft.paymentMode,
  ]);

  const destinationBusinessUnits = useMemo(() => {
    const businessUnitMap = new Map<string, BusinessUnitInterface>();

    businessUnits.forEach((bu) => {
      businessUnitMap.set(bu.code, {
        ...bu,
        isLocked: false,
        disabled: false,
        tooltip: "",
      });
    });

    corporateDestinationBUs.forEach((bu) => {
      businessUnitMap.set(bu.code, bu);
    });

    return Array.from(businessUnitMap.values());
  }, [businessUnits, corporateDestinationBUs]);

  const estimatedDeliveryDate = useMemo(() => {
    if (
      estimatedDeliveryTime === undefined ||
      (shipmentDraft.service !== ShipmentService.ECONOMIC &&
        shipmentDraft.service !== ShipmentService.SAVER)
    )
      return;

    const holidaySet = new Set(
      holidays.map((h) => moment(h.date).format("YYYY-MM-DD"))
    );
    let remainingDays = estimatedDeliveryTime;
    let currentDay = moment();

    while (remainingDays > 0) {
      currentDay = currentDay.add(1, "days");

      if (
        currentDay.weekday() !== 5 &&
        currentDay.weekday() !== 6 &&
        !holidaySet.has(currentDay.format("YYYY-MM-DD"))
      ) {
        remainingDays -= 1;
      }
    }

    return currentDay.format("YYYY-MM-DD");
  }, [estimatedDeliveryTime, holidays, shipmentDraft.service]);

  const clean = () => {
    //setDisablePaymentMode(false);
    setInternational(false);
    setBaseShipmentItems([]);
    setShipmentItems([]);
    setErrors({
      shipper: "",
      consignee: "",
      location: "",
      declaredValue: "",
      items: "",
      pieces: false,
    });
    saveDraft({
      id: undefined,
      shipmentNumber: undefined,
      document: undefined,
      declaredValue: "",
      deliveryType: DeliveryType.AT_OFFICE,
      service: ShipmentService.STANDARD,
      preShipmentHeaderID: undefined,
      shipper: index > 0 ? shipmentDraft.shipper : undefined,
      consignee: undefined,
      shipperAddress: undefined,
      consigneeAddress: undefined,
      buConsignee: undefined,
      pieces: [],
      items: [],
      buSource: user.businessUnit,
      paymentMode: undefined,
      discount: undefined,
      status: ShipmentStatus.DRAFT,
      accountSiteID: undefined,
      accountBillTo: undefined,
      boxAccount: undefined,
      destination: "",
      observations: "",
      isInternational: false,
      pickupType: undefined,
    });
    setCleaned(true);
  };

  const deleteDraft = () => {
    const newDrafts = [...shipmentDrafts];
    newDrafts.splice(index, 1);
    dispatch(setShipmentCreateDrafts(newDrafts));
    onDelete();
  };

  const handleDropdownMenuSelect = (option: { value: DropdownMenuOption }) => {
    if (option.value === DropdownMenuOption.EDIT_SHIPPER) {
      setClientTypeSelected("shipper");
      setOpenUpdateModal(true);
    } else if (option.value === DropdownMenuOption.EDIT_CONSIGNEE) {
      setClientTypeSelected("consignee");
      setOpenUpdateModal(true);
    } else if (option.value === DropdownMenuOption.CLEAN) {
      setOpenCleanModal(true);
    } else if (option.value === DropdownMenuOption.DELETE) {
      setOpenDeleteModal(true);
    } else if (option.value === DropdownMenuOption.CANCEL) {
      setOpenCancelModal(true);
    }
  };

  const handlePaymentModeChange = async (
    option?: PaymentMode | number | string
  ) => {
    console.log(option);
    if (!option) return;

    let paymentMode: PaymentMode;
    let accountBillTo: AccountInterface | undefined;
    let accountSiteID: number | undefined;

    if (option === PaymentMode.COD) {
      paymentMode = PaymentMode.COD;
      accountBillTo = shipmentDraft.consignee;
      accountSiteID = undefined;
    } else if (option === PaymentMode.CONTADO) {
      paymentMode = PaymentMode.CONTADO;
      accountBillTo = shipmentDraft.shipper;
      accountSiteID = undefined;
    } else if (option === PaymentMode.BOXOFFICE_CREDIT) {
      paymentMode = PaymentMode.BOXOFFICE_CREDIT;
      accountBillTo = shipmentDraft.shipper;
      accountSiteID = undefined;
    } else {
      paymentMode = PaymentMode.CREDIT;
      accountBillTo = paymentModesWithAuth.find(
        (p) =>
          typeof p.value === "object" &&
          `${p.value.id}-${p.value.accountSiteID}` === option
      )?.value! as AccountInterface;
      accountSiteID = accountBillTo.accountSiteID ?? undefined;
    }

    saveDraft({
      accountBillTo,
      paymentMode,
      accountSiteID,
    });
  };

  const handlePreShipmentSubmit = async () => {
    if (!preShipmentCode) {
      setPreShipmentError("Ingrese un código de Pre-Guía");
      return;
    }

    loaderService.start();
    const preShipmentMessage = await getPreShipment(
      preShipmentCode,
      pieceCategories.find((p) => p.code === "0490")!
    );
    loaderService.stop();

    if (preShipmentMessage.didError || preShipmentMessage.model === null) {
      setPreShipmentError(preShipmentMessage.errorMessage);
      return;
    }

    const preShipment = preShipmentMessage.model;
    const shipper = await getAccount(preShipment?.shipper?.id ?? "");
    const consignee = await getAccount(preShipment?.consignee?.id ?? "");
    setDeclaredValue(preShipment.declaredValue ?? "");

    // Verify account bill to
    let accountBillTo: AccountInterface | undefined;
    const paymentMode = preShipment.paymentMode!;
    if (!preShipment.accountBillTo) {
      if (
        paymentMode === PaymentMode.COD ||
        paymentMode === PaymentMode.DEST_BOXOFFICE_CREDIT
      ) {
        accountBillTo = consignee ?? undefined;
      } else if (
        paymentMode === PaymentMode.CONTADO ||
        paymentMode === PaymentMode.BOXOFFICE_CREDIT
      ) {
        accountBillTo = shipper ?? undefined;
      } else {
        setPreShipmentError(
          "La pre-guía es de crédito pero no tiene cuenta a la cual facturar."
        );
        return;
      }
    } else {
      accountBillTo =
        shipper?.listAuthorizingAccount.find(
          (a) => a.id === preShipment.accountBillTo?.id
        ) ??
        (await getAccount(preShipment?.accountBillTo?.id ?? "")) ??
        undefined;
    }

    const buConsignee = businessUnits.find(
      (bu) => bu.code === preShipment?.buConsignee?.code
    );

    // Rewrite declared value of pieces to 0 if the payment mode is credit and
    // the creation user is mercado libre
    if (
      preShipment.paymentMode === PaymentMode.CREDIT &&
      preShipment.creationUser === mercadoLibreUser
    ) {
      if (!!preShipment.pieces && preShipment.pieces?.length > 0) {
        for (let i = 0; i < preShipment.pieces.length; i++) {
          preShipment.pieces[i].value = 0;
        }
      }
    }

    // Get shipper address
    let shipperAddress: LocationInterface | undefined;
    if (preShipment.pickupType === DeliveryType.AT_OFFICE) {
      shipperAddress = user.businessUnit?.location;
    } else if (
      preShipment.pickupType === DeliveryType.AT_HOME &&
      preShipment.shipperAddress?.coordinates?.lat !== 0 &&
      preShipment.shipperAddress?.coordinates?.lng !== 0
    ) {
      getDetailsFromCoords(
        preShipment.shipperAddress?.coordinates?.lat!,
        preShipment.shipperAddress?.coordinates?.lng!,
        mapRef,
        (location) => {
          const reference = preShipment.shipperAddress?.name;
          shipperAddress = location;
          shipperAddress.reference = reference;
          saveDraft({ shipperAddress });
        }
      );
    } else if (
      preShipment.pickupType === DeliveryType.AT_HOME &&
      preShipment.shipperAddress
    ) {
      shipperAddress = undefined;
    }

    // Get consignee address
    let consigneeAddress: LocationInterface | undefined;
    if (preShipment.deliveryType === DeliveryType.AT_OFFICE) {
      consigneeAddress = buConsignee?.location;
    } else if (
      preShipment.deliveryType === DeliveryType.AT_HOME &&
      preShipment.consigneeAddress?.coordinates?.lat !== 0 &&
      preShipment.consigneeAddress?.coordinates?.lng !== 0
    ) {
      getDetailsFromCoords(
        preShipment.consigneeAddress?.coordinates?.lat!,
        preShipment.consigneeAddress?.coordinates?.lng!,
        mapRef,
        (location) => {
          const reference = preShipment.consigneeAddress?.name;
          consigneeAddress = location;
          consigneeAddress.reference = reference;
          saveDraft({ consigneeAddress });
        }
      );
    } else if (
      preShipment.deliveryType === DeliveryType.AT_HOME &&
      preShipment.consigneeAddress
    ) {
      consigneeAddress = undefined;
      setAuxReference(preShipment.consigneeAddress.name);
    }

    const draft: Partial<ShipmentInterface> = {
      ...preShipment,
      id: undefined,
      buConsignee: buConsignee ?? undefined,
      shipper: shipper ?? undefined,
      consignee: consignee ?? undefined,
      accountBillTo: accountBillTo,
      paymentMode: paymentMode,
      preShipmentHeaderID: preShipment.id,
      buSource: user.businessUnit,
      shipperAddress,
      consigneeAddress,
    };
    console.log(draft);
    saveDraft(draft);
    setOpenPreShipmentModal(false);
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 1) {
      event.target.value = event.target.value.replace(/^0+/, "");
    }
    const setValue = (value: string) => {
      event.target.value = value;
    };

    if (!formatFloat(event.target.value, setValue)) {
      return false;
    }
    return true;
  };

  // Get DHL rate for international shipments
  const getDHLrates = async (
    declaredValue: number,
    postalCode?: string,
    countyName?: string
  ) => {
    const { deliveryType, service } = shipmentDraft;

    if (deliveryType === DeliveryType.INTERNATIONAL && !!shipmentDraft.pieces) {
      const selector = "[name='reference']";
      const { packages } = convertPieceToDhlPackageDto(shipmentDraft.pieces);
      const input = document.querySelector(selector) as HTMLInputElement;
      const referencePoint = input.value;
      const [address1, address2, address3] = splitAddress(referencePoint);

      // Block the flow while the DHL tariff is being fetched: loaderService
      // disables "Continuar" so the user cannot proceed on the stale fallback
      // rate. The items effect keeps the loader on afterwards while
      // applyInternationalRate copies the new rate onto the flete line.
      loaderService.start();
      setIsLoading((p) => p + 1);
      try {
        const DHLrates = await getRatesDHL({
          receiverPostalCode: postalCode,
          receiverCountyName: countyName,
          receiverCityName: shipmentDraft.cityName ?? "",
          receiverCountryCode: shipmentDraft.countryCode ?? "",
          receiverAddressLine1: address1,
          receiverAddressLine2: address2,
          receiverAddressLine3: address3,
          packages: packages,
          declaredValue: declaredValue,
          isCustomsDeclarable:
            service === ShipmentService.STANDARD_INTERNATIONAL,
        });

        if (DHLrates) {
          saveDraft({
            dhlItem: convertRatesDHLResponseToItemInterface(
              DHLrates,
              exchanges
            ),
          });
        }
      } finally {
        setIsLoading((p) => p - 1);
        loaderService.stop();
      }
    }
  };

  // True while an international DHL re-quote is scheduled or in flight. Drives
  // the loader so "Continuar" is disabled from the first keystroke in the
  // address until the new tariff has been fetched and applied — the user can
  // never proceed on the stale fallback rate.
  const [dhlQuotePending, setDhlQuotePending] = useState(false);
  // Identifies the latest re-quote so a slow response from a superseded request
  // cannot clear the pending flag while a fresher edit is still pending.
  const dhlQuoteToken = useRef(0);

  // Mirror the pending flag onto the global loader. loaderService is
  // counter-based, so the cleanup balances each start.
  useEffect(() => {
    if (!dhlQuotePending) {
      return;
    }
    loaderService.start();
    return () => loaderService.stop();
  }, [dhlQuotePending]);

  // Re-quote DHL shortly after the user stops typing the international address.
  // Editing "Dirección" (consigneeAddress.reference) changes neither
  // dhlDestination nor pieces, so the getDHLrates effect below does not re-run.
  // Debounce here so the flete amount refreshes right after typing stops, and
  // mark the quote pending on the first keystroke so the loader blocks
  // "Continuar" throughout (debounce wait -> fetch -> applyInternationalRate).
  useEffect(() => {
    const { deliveryType, dhlDestination, pieces } = shipmentDraft;
    if (
      deliveryType !== DeliveryType.INTERNATIONAL ||
      !dhlDestination ||
      !pieces ||
      pieces.length === 0
    ) {
      // Not an international quote anymore: release any pending block.
      setDhlQuotePending(false);
      return;
    }

    const token = ++dhlQuoteToken.current;
    setDhlQuotePending(true);

    const handler = setTimeout(async () => {
      try {
        await getDHLrates(
          Number(declaredValue),
          dhlDestination.postalCode,
          dhlDestination.countyName
        );
      } finally {
        // Only release the block if no newer edit superseded this request.
        if (dhlQuoteToken.current === token) {
          setDhlQuotePending(false);
        }
      }
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentDraft.consigneeAddress?.reference]);

  const verifyDeclaredValue = useCallback(
    (value: string) => {
      if (
        shipmentDraft.boxAccount ||
        shipmentDraft.service === ShipmentService.DOCUMENT ||
        shipmentDraft.paymentMode === PaymentMode.CREDIT
      ) {
        return true;
      }

      const minVal = minDeclaredValue(
        shipmentDraft.totalChargedWeight ?? 0,
        rangeMinDeclareList,
        shipmentDraft.service
      );

      if (parseFloat(value ? value : "0") < minVal) {
        setErrors((errors) => ({
          ...errors,
          declaredValue: `El valor declarado no puede ser menor a ${minVal.toFixed(
            2
          )}$`,
        }));
        return false;
      } else {
        setErrors((errors) => ({
          ...errors,
          declaredValue: "",
        }));
        return true;
      }
    },
    [shipmentDraft, rangeMinDeclareList]
  );

  const handleDeclaredValueChange = () => {
    const deliveryType = shipmentDraft.deliveryType;
    const value = parseFloat(declaredValue ? declaredValue : "0");

    if (!(deliveryType === DeliveryType.INTERNATIONAL)) {
      if (!verifyDeclaredValue(declaredValue ? declaredValue : "0")) {
        return;
      }
    }
    saveDraft({
      declaredValue,
      pieces: shipmentDraft.pieces!.map((piece) => ({
        ...piece,
        value: value / shipmentDraft.pieces!.length,
      })),
    });

    if (deliveryType === DeliveryType.INTERNATIONAL) {
      getDHLrates(
        Number(declaredValue),
        shipmentDraft.dhlDestination?.postalCode,
        shipmentDraft.dhlDestination?.countyName
      );
    }
  };

  const updateInputValue = () => {
    if (countryName) {
      const input = document.querySelector(
        '[placeholder="Buscar país..."]'
      ) as HTMLInputElement;

      if (input) {
        input.value = countryName;
      } else {
        console.error("Input element not found");
      }
    } else {
      console.error("countryName is not defined or has no value");
    }
  };

  useEffect(() => {
    const buCode = user.businessUnit?.code;
    if (!buCode) {
      setLockBUPaymentModes([]);
      return;
    }

    const afunction = async () => {
      const response = await getLockBUPaymentModes(buCode);

      if (response.didError || !response.model) {
        setLockBUPaymentModes([]);
        return;
      }

      setLockBUPaymentModes(response.model);
    };

    afunction();
  }, [user.businessUnit?.code]);

  //Get DHL location predictions
  useEffect(() => {
    if (
      shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL &&
      shipmentDraft.dhlDestination &&
      shipmentDraft.pieces &&
      shipmentDraft.pieces.length > 0
    ) {
      getDHLrates(
        Number(declaredValue),
        shipmentDraft.dhlDestination.postalCode,
        shipmentDraft.dhlDestination.countyName
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shipmentDraft.deliveryType,
    shipmentDraft.service,
    shipmentDraft.pieces,
    shipmentDraft.dhlDestination,
  ]);

  //Get DHL location predictions
  useEffect(() => {
    const countryCode = shipmentDraft.countryCode;
    const cityValue = shipmentDraft.cityValue;

    if (!countryCode || !cityValue || cityValue.length < 3) {
      setCityOptions(undefined);
      return;
    }

    const timerId = setTimeout(async () => {
      setLoader(true);
      const addressOptions = await addressValidation(countryCode, cityValue);
      if (addressOptions) {
        setCityOptions(addressOptions);
      }
      setLoader(false);
    }, 2000);

    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentDraft.cityValue]);

  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCityOptions(undefined);
    saveDraft({
      cityValue: event.target.value,
    });
  };

  const handleAnulation = async () => {
    if (!shipmentDraft?.id) return;

    const response = await updateShipmentStatus(
      ShipmentStatus.ANNULLED,
      shipmentDraft?.id,
      shipmentDraft?.shipmentNumber ?? "",
      user.user ?? undefined
    );

    if (response.didError) {
      alertService.error(
        "Error al anular la guía",
        response.errorMessage ?? "No se pudo anular la guía."
      );
    } else {
      alertService.success("Guía anulada", "La guía se anuló correctamente.");
      setOpenCancelModal(false);

      if (shipmentDrafts.length === 1) {
        clean();
      } else {
        deleteDraft();
      }
    }
  };

  // Get shipper auth
  useEffect(() => {
    const setShipperAuth = (auth: AccountAuthorization) => {
      saveDraft({ shipperAuth: auth });
    };

    if (!shipmentDraft.shipper) {
      setShipperAuth(AccountAuthorization.NO_AUTHORIZED);
      return;
    }

    if (
      !!shipmentDraft.shipper.agreementID ||
      shipmentDraft.shipper.listAuthorizingAccount.length > 0
    ) {
      saveDraft({
        shipperAuth: AccountAuthorization.ENTERPRISE,
      });

      return;
    } else {
      saveDraft({
        shipperAuth: AccountAuthorization.ENTERPRISE,
      });
    }

    if (shipmentDraft.accountBillTo?.agreementID) {
      setShipperAuth(AccountAuthorization.ENTERPRISE);
      return;
    }

    // Check if there are some autorizers companies
    if (shipmentDraft.shipper.listAuthorizingAccount.length === 0) {
      setShipperAuth(AccountAuthorization.NO_AUTHORIZED);
      return;
    }

    // If there is no elements in the shippers listAuthorizingAccount then, set AUTHORIZED
    setShipperAuth(AccountAuthorization.AUTHORIZED);
  }, [shipmentDraft.shipper, shipmentDraft.accountBillTo?.agreementID, saveDraft]);

  useEffect(() => {
    if (!!shipmentDraft.boxAccount) {
      setConsigneeType("locker");
    }
  }, [shipmentDraft.boxAccount]);

  // Get consignee auth
  useEffect(() => {
    const setConsigneeAuth = (auth: AccountAuthorization) => {
      saveDraft({ consigneeAuth: auth });
    };

    if (!shipmentDraft.consignee) {
      setConsigneeAuth(AccountAuthorization.NO_AUTHORIZED);
      return;
    }

    // Check if there are some autorizers companies
    if (!!shipmentDraft.consignee.agreementID) {
      setConsigneeAuth(AccountAuthorization.ENTERPRISE);
      return;
    }

    // If there is no elements in the shippers listAuthorizingAccount then, set AUTHORIZED
    if (shipmentDraft.consignee.listAuthorizingAccount.length === 0) {
      setConsigneeAuth(AccountAuthorization.NO_AUTHORIZED);
      return;
    }

    setConsigneeAuth(AccountAuthorization.AUTHORIZED);
  }, [shipmentDraft.consignee, saveDraft]);

  // Get whitelist from selected shipper
  useEffect(() => {
    let canceled = false;

    const getLocations = async () => {
      if (
        !shipmentDraft.accountBillTo ||
        shipmentDraft.shipperAuth === AccountAuthorization.NO_AUTHORIZED ||
        (!shipmentDraft.accountBillTo.agreementID &&
          (shipmentDraft.accountBillTo.listAuthorizingAccount ?? []).length ===
            0)
      ) {
        setWhitelist([]);
        return;
      }

      const response = await getAccountWhitelist(
        taxIdTypes,
        shipmentDraft.accountBillTo.id,
        shipmentDraft.accountBillTo?.agreementID ?? undefined,
        false // isLocked = false - Only get whitelisted destinations
      );
      if (canceled) return;

      if (response.didError || !response.model) {
        setErrors((errors) => ({
          ...errors,
          consignee: response.errorMessage,
        }));
        setWhitelist([]);
        return;
      }

      setWhitelist(response.model.consigneeDestinationList);
    };

    getLocations();

    return () => {
      canceled = true;
    };
  }, [shipmentDraft.accountBillTo, shipmentDraft.shipperAuth, taxIdTypes]);

  useEffect(() => {
    let canceled = false;

    const getCorporateDestinationBUs = async () => {
      const authAccounts = [
        ...(shipmentDraft.shipper?.listAuthorizingAccount ?? []),
        ...(shipmentDraft.accountBillTo?.listAuthorizingAccount ?? []),
      ];

      const accountSiteIDs = Array.from(
        new Set(
          authAccounts
            .map((account) => account.accountSiteID)
            .filter((accountSiteID): accountSiteID is number =>
              Number.isFinite(accountSiteID)
            )
        )
      );

      if (accountSiteIDs.length === 0) {
        setAccountSiteBUsMap({});
        setAccountSiteHasBURestrictionsMap({});
        setAccountSiteDeliveryTypeLocksMap({});
        setCorporateDestinationBUs([]);
        return;
      }

      const buResponses = await Promise.all(
        accountSiteIDs.map((accountSiteID) =>
          getAccountSiteBUByAccountSiteID(String(accountSiteID))
        )
      );

      const lockResponses = await Promise.all(
        accountSiteIDs.map((accountSiteID) =>
          GetAccountSiteLockByAccountSiteID(String(accountSiteID))
        )
      );

      if (canceled) return;

      const buInfoByCode = new Map<
        string,
        { hasDestination: boolean; hasUnlockedDestination: boolean }
      >();
      const nextAccountSiteBUsMap: Record<number, string[]> = {};
      const nextAccountSiteHasBURestrictionsMap: Record<number, boolean> = {};
      const nextAccountSiteDeliveryTypeLocksMap: Record<number, number[]> = {};

      buResponses.forEach((response, index) => {
        const accountSiteID = accountSiteIDs[index];

        if (response.didError || !response.model) return;

        nextAccountSiteBUsMap[accountSiteID] = [];

        response.model.forEach((accountSiteBU) => {
          const rawBU = accountSiteBU as unknown as {
            BUCode?: string;
            IsSource?: boolean;
            IsLocked?: boolean;
          };
          const buCode = rawBU.BUCode ?? accountSiteBU.buCode;

          if (buCode) {
            const currentInfo = buInfoByCode.get(buCode) ?? {
              hasDestination: false,
              hasUnlockedDestination: false,
            };

            const isLocked = Boolean(rawBU.IsLocked ?? accountSiteBU.isLocked);
            const isSource = Boolean(rawBU.IsSource ?? accountSiteBU.isSource);

            buInfoByCode.set(buCode, {
              hasDestination: currentInfo.hasDestination || !isSource,
              hasUnlockedDestination:
                currentInfo.hasUnlockedDestination || (!isSource && !isLocked),
            });

            if (!isSource && !nextAccountSiteBUsMap[accountSiteID].includes(buCode)) {
              nextAccountSiteBUsMap[accountSiteID].push(buCode);
            }
          }
        });
        
        nextAccountSiteHasBURestrictionsMap[accountSiteID] =
          nextAccountSiteBUsMap[accountSiteID].length > 0;
      });

      lockResponses.forEach((response, index) => {
        const accountSiteID = accountSiteIDs[index];

        if (response.didError || !response.model) {
          nextAccountSiteDeliveryTypeLocksMap[accountSiteID] = [];
          return;
        }

        nextAccountSiteDeliveryTypeLocksMap[accountSiteID] = Array.from(
          new Set(
            response.model
              .map((lock) => Number(lock.deliveryTypeID))
              .filter((deliveryTypeID) => Number.isFinite(deliveryTypeID))
          )
        );
      });

      setAccountSiteBUsMap(nextAccountSiteBUsMap);
      setAccountSiteHasBURestrictionsMap(nextAccountSiteHasBURestrictionsMap);
      setAccountSiteDeliveryTypeLocksMap(nextAccountSiteDeliveryTypeLocksMap);

      const destinationBUs = Array.from(buInfoByCode.entries())
        .filter(([, info]) => info.hasDestination)
        .map(([buCode, info]) => {
        const isDisabled = !info.hasUnlockedDestination;
        const existingBU = businessUnits.find((bu) => bu.code === buCode);

        if (existingBU) {
          return {
            ...existingBU,
            isLocked: isDisabled,
            disabled: isDisabled,
            tooltip: isDisabled ? "Destino deshabilitado" : "",
            type:
              existingBU.type === BusinessType.EXTERNAL ||
              existingBU.type === BusinessType.INTERNAL
                ? existingBU.type
                : BusinessType.INTERNAL,
          };
        }

        return {
          id: `account-site-${buCode}`,
          name: buCode,
          code: buCode,
          type: BusinessType.INTERNAL,
          isLocked: isDisabled,
          disabled: isDisabled,
          tooltip: isDisabled ? "Destino deshabilitado" : "",
          polygonList: [],
          location: {
            name: buCode,
            address: "",
            coordinates: {
              lat: 0,
              lng: 0,
            },
          },
        };
        });

      setCorporateDestinationBUs(destinationBUs);
    };

    getCorporateDestinationBUs();

    return () => {
      canceled = true;
    };
  }, [
    shipmentDraft.shipper?.listAuthorizingAccount,
    shipmentDraft.accountBillTo?.listAuthorizingAccount,
    businessUnits,
  ]);

  useEffect(() => {
    const creditOptions = paymentModesWithAuth
      .map((option) => option.value)
      .filter(
        (value): value is AccountInterface => typeof value === "object"
      );

    const firstCreditOption = creditOptions.find((option) => !!option.accountSiteID);
    const selectedBUCode = shipmentDraft.buConsignee?.code;
    const isDefaultBusinessUnit =
      !selectedBUCode || businessUnits.some((bu) => bu.code === selectedBUCode);

    if (!isDefaultBusinessUnit) {
      if (!firstCreditOption) {
        if (
          shipmentDraft.paymentMode !== undefined ||
          shipmentDraft.accountBillTo !== undefined ||
          shipmentDraft.accountSiteID !== undefined
        ) {
          saveDraft({
            paymentMode: undefined,
            accountBillTo: undefined,
            accountSiteID: undefined,
          });
        }
        return;
      }

      const firstCreditKey = `${firstCreditOption.id}-${firstCreditOption.accountSiteID}`;
      const selectedCreditKey =
        shipmentDraft.paymentMode === PaymentMode.CREDIT &&
        shipmentDraft.accountBillTo &&
        shipmentDraft.accountSiteID
          ? `${shipmentDraft.accountBillTo.id}-${shipmentDraft.accountSiteID}`
          : "";

      if (selectedCreditKey !== firstCreditKey) {
        saveDraft({
          paymentMode: PaymentMode.CREDIT,
          accountBillTo: firstCreditOption,
          accountSiteID: firstCreditOption.accountSiteID ?? undefined,
        });
      }
      return;
    }

    if (
      shipmentDraft.paymentMode !== PaymentMode.CREDIT ||
      !shipmentDraft.accountBillTo ||
      !shipmentDraft.accountSiteID
    ) {
      return;
    }

    const selectedCreditKey = `${shipmentDraft.accountBillTo.id}-${shipmentDraft.accountSiteID}`;
    const availableCreditKeys = creditOptions.map(
      (account) => `${account.id}-${account.accountSiteID}`
    );

    if (!availableCreditKeys.includes(selectedCreditKey)) {
      saveDraft({
        paymentMode: PaymentMode.CONTADO,
        accountBillTo: shipmentDraft.shipper,
        accountSiteID: undefined,
      });
    }
  }, [
    businessUnits,
    shipmentDraft.paymentMode,
    shipmentDraft.buConsignee?.code,
    shipmentDraft.shipper,
    shipmentDraft.accountBillTo,
    shipmentDraft.accountSiteID,
    paymentModesWithAuth,
    saveDraft,
  ]);

  // Get whitelist text from selected consignee
  useEffect(() => {
    setWhitelistSearch(
      formatName(shipmentDraft.consignee?.accountFullName ?? "")
    );
  }, [shipmentDraft.consignee]);

  // Get shipment items
  useEffect(() => {
    const { totalChargedWeight, declaredValue, deliveryDistance } =
      shipmentDraft;

    // Unchanged shipment returning to the form: keep the persisted quote and
    // skip the redundant items refetch (which would feed a rate recompute).
    // Leave baseShipmentItems as-is — the rate effect short-circuits too, so
    // the previous quote stays untouched.
    if (
      (shipmentDraft.items?.length ?? 0) > 0 &&
      !!shipmentDraft.quoteSignature &&
      shipmentDraft.quoteSignature === buildQuoteSignature()
    ) {
      return;
    }

    if (
      !totalChargedWeight ||
      deliveryDistance === undefined ||
      (shipmentDraft.service !== ShipmentService.DOCUMENT &&
        shipmentDraft.paymentMode !== PaymentMode.CREDIT &&
        (!declaredValue || isNaN(Number(declaredValue)))) ||
      !shipmentDraft.buSource?.location?.coordinates.lat ||
      !shipmentDraft.buSource?.location?.coordinates.lng ||
      !shipmentDraft.consigneeAddress?.coordinates.lat ||
      !shipmentDraft.consigneeAddress?.coordinates.lng ||
      !tier
    ) {
      setBaseShipmentItems([]);
      return;
    }

    setErrors((errors) => ({
      ...errors,
      items: "",
    }));

    const getAllShipmentItems = async () => {
      const key = v4();
      itemsKey.current = key;

      loaderService.start();
      setIsLoading((p) => p + 1);
      const response = await getShipmentItems({
        CurrencyID: 2,
        CountryID: 236,
        Weight: totalChargedWeight,
        DeclaratedValue: declaredValue ? Number(declaredValue) : 0,
        Distance: deliveryDistance,
        ApplicationID: applicationID,
        ShipperAccountID: shipmentDraft.shipper?.id,
        ConsigneeAccountID: shipmentDraft.consignee?.id,
        AccountBillToID: shipmentDraft.accountBillTo?.id,
        AccountSiteID: shipmentDraft.accountBillTo?.accountSiteID ?? undefined,
        ServiceID: shipmentDraft.service,
        PaymentModeID: shipmentDraft.paymentMode,
        DeliveryTypeID: shipmentDraft.deliveryType,
        SRM: !!shipmentDraft.pieces?.reduce((acc, p) => acc + p.value, 0),
        TierID: tier?.tierID,
        ShippingMethodID: 10,
        PackageTypeID: 10,
        SalesDate: new Date().toISOString(),
        BUCodeSource: shipmentDraft.buSource?.code,
        BUCodeConsignee: shipmentDraft.buConsignee?.code,
        PickupTypeID: shipmentDraft.pickupType,
      });
      setIsLoading((p) => p - 1);
      loaderService.stop();

      if (!response || key !== itemsKey.current) return;
      if (response.didError || !response.model) {
        setErrors((errors) => ({
          ...errors,
          items: response.errorMessage,
        }));
        return;
      }
      itemsKey.current = "";

      // Filter ipostel and discount
      setBaseShipmentItems(
        response.model.filter(
          (item) =>
            !item.name.toLowerCase().includes("ipostel") &&
            item.id !== discountItem.id &&
            (item.mandatory ||
              shipmentDraft.items?.some((i) => i.id === item.id))
        )
      );
    };

    getAllShipmentItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tier,
    shipmentDraft.shipper?.id,
    shipmentDraft.consignee?.id,
    shipmentDraft.service,
    shipmentDraft.paymentMode,
    shipmentDraft.deliveryType,
    shipmentDraft.pieces,
    shipmentDraft.totalChargedWeight,
    shipmentDraft.declaredValue,
    shipmentDraft.deliveryDistance,
    shipmentDraft.buSource,
    shipmentDraft.consigneeAddress?.coordinates.lat,
    shipmentDraft.consigneeAddress?.coordinates.lng,
    applicationID,
    // NOTE: buildQuoteSignature intentionally omitted (see the rate effect for
    // the rationale): it changes identity on every saveDraft and would loop.
    // The signature is computed inside the effect at run time.
  ]);

  useEffect(() => {
    if (!shipmentDraft.pieces || shipmentDraft.pieces.length === 0) {
      saveDraft({
        totalChargedWeight: 0,
        totalPhysicalWeight: 0,
        totalDimensionalWeight: 0,
        dimensionalFactor: 0,
      });
      return;
    }

    const getShipmentWeight = async () => {
      const weights = await getWeights(
        236,
        10,
        shipmentDraft.shipper?.id,
        shipmentDraft.accountSiteID ?? undefined,
        shipmentDraft.service,
        shipmentDraft.paymentMode,
        shipmentDraft.pieces ?? []
      );

      if (!weights || weights.didError) {
        saveDraft({
          totalChargedWeight: 0,
          totalPhysicalWeight: 0,
          totalDimensionalWeight: 0,
          dimensionalFactor: 0,
        });
        return;
      }

      saveDraft({
        totalChargedWeight: weights.model?.chargedWeight,
        totalPhysicalWeight: weights.model?.physicalWeight,
        totalDimensionalWeight: weights.model?.dimensionalWeight,
        dimensionalFactor: weights.model?.volumetricFactor,
      });
    };

    getShipmentWeight();
  }, [
    shipmentDraft.pieces,
    shipmentDraft.service,
    shipmentDraft.shipper?.id,
    shipmentDraft.accountSiteID,
    shipmentDraft.paymentMode,
    saveDraft,
  ]);

  useEffect(() => {
    if (
      !shipmentDraft.buConsignee?.location?.coordinates.lat ||
      !shipmentDraft.buConsignee?.location?.coordinates.lng ||
      !shipmentDraft.consigneeAddress?.coordinates.lat ||
      !shipmentDraft.consigneeAddress?.coordinates.lng
    ) {
      saveDraft({
        deliveryDistance: 0,
      });
      return;
    }

    const getShipmentDeliveryDistance = async () => {
      const distance = await getDeliveryDistance(
        shipmentDraft.buConsignee?.location?.coordinates.lat!,
        shipmentDraft.buConsignee?.location?.coordinates.lng!,
        shipmentDraft.consigneeAddress?.coordinates.lat!,
        shipmentDraft.consigneeAddress?.coordinates.lng!
      );

      if (!distance || distance.didError) return;

      saveDraft({ deliveryDistance: distance.model?.deliveryDistance });
    };

    getShipmentDeliveryDistance();
  }, [shipmentDraft.buConsignee, shipmentDraft.consigneeAddress, saveDraft]);

  useEffect(() => {
    if (
      !shipmentDraft.buSource?.location?.coordinates.lat ||
      !shipmentDraft.buSource?.location?.coordinates.lng ||
      !shipmentDraft.shipperAddress?.coordinates.lat ||
      !shipmentDraft.shipperAddress?.coordinates.lng
    ) {
      saveDraft({
        pickupDistance: 0,
      });
      return;
    }

    const getShipmentDeliveryDistance = async () => {
      const distance = await getDeliveryDistance(
        shipmentDraft.buSource?.location?.coordinates.lat!,
        shipmentDraft.buSource?.location?.coordinates.lng!,
        shipmentDraft.shipperAddress?.coordinates.lat!,
        shipmentDraft.shipperAddress?.coordinates.lng!
      );

      if (!distance || distance.didError) return;

      saveDraft({ pickupDistance: distance.model?.deliveryDistance });
    };

    getShipmentDeliveryDistance();
  }, [shipmentDraft.buSource, shipmentDraft.shipperAddress, saveDraft]);

  // Get shipment items rate
  useEffect(() => {
    // Preserve the previously computed quote when returning to this form
    // (e.g. Regresar) with no rate-relevant change. buildQuoteSignature
    // excludes saleDate, so an unchanged shipment yields the same signature
    // and we skip getShipmentRates entirely (no network, no overwrite, no
    // flicker). This MUST run before the blank guard below, otherwise the
    // momentarily-empty baseShipmentItems would wipe the persisted quote.
    const currentSignature = buildQuoteSignature();
    if (
      !!shipmentDraft.quoteSignature &&
      shipmentDraft.quoteSignature === currentSignature &&
      (shipmentDraft.items?.length ?? 0) > 0
    ) {
      return;
    }

    // First rate-relevant change to an already-saved guide after returning from
    // the Order screen: release the promotion the backend still holds for it
    // (PUT with base+mandatory items only) before the rate is recomputed, so
    // any promotion tied to it is freed. Fires once per save cycle — the flag
    // is reset to false when the guide is (re)saved on Continuar.
    if (
      !!shipmentDraft.shipmentNumber &&
      !!shipmentDraft.quoteSignature &&
      shipmentDraft.quoteSignature !== currentSignature &&
      !shipmentDraft.promotionReleased
    ) {
      saveDraft({ promotionReleased: true });
      releaseShipmentPromotion();
    }

    setErrors((errors) => ({
      ...errors,
      items: "",
    }));

    const {
      pieces,
      buSource,
      consigneeAddress,
      shipperAddress,
      shipper,
      totalChargedWeight,
    } = shipmentDraft;
    const serviceID = shipmentDraft.service;
    // Hard-invalid inputs: the shipment cannot be quoted at all. Wipe the quote
    // AND its signature so a later valid state always recomputes.
    if (
      !pieces ||
      !shipper ||
      !buSource ||
      !serviceID ||
      !consigneeAddress ||
      pieces.length === 0 ||
      (!!service?.maxWeight &&
        !!totalChargedWeight &&
        totalChargedWeight > service.maxWeight)
    ) {
      setShipmentItems([]);
      saveDraft({ items: [], quoteSignature: undefined });
      return;
    }
    // Transient: base items not loaded yet (e.g. mid-change refetch). The
    // unchanged-return case already short-circuited above, so reaching here
    // means a genuine recompute is pending; clear the stale quote until it
    // arrives. Keep the signature so we don't churn it needlessly.
    if (baseShipmentItems.length === 0) {
      setShipmentItems([]);
      saveDraft({ items: [] });
      return;
    }

    const getAllShipmentItems = async () => {
      const key = v4();
      rateKey.current = key;

      loaderService.start();
      setIsLoading((p) => p + 1);

      let response = await getShipmentRates(
        serviceID,
        shipmentDraft.paymentMode ?? PaymentMode.COD,
        shipmentDraft.deliveryType ?? DeliveryType.AT_OFFICE,
        buSource.code ?? "",
        shipmentDraft.buConsignee?.code ?? "",
        shipmentDraft.shipper?.id,
        shipmentDraft.consignee?.id,
        shipmentDraft.accountBillTo?.id,
        shipmentDraft.accountSiteID ?? undefined,
        shipperAddress ?? buSource.location,
        consigneeAddress,
        baseShipmentItems,
        pieces,
        pieces.reduce((acc, p) => acc + p.value, 0) > 0,
        new Date().toISOString(),
        applicationID
      );
      setIsLoading((p) => p - 1);
      loaderService.stop();

      if (!response || key !== rateKey.current) {
        return;
      }
      if (response.didError || !response.model) {
        alertService.error(
          "Hubo un error calculando las tarifas.",
          "Por favor espere unos segundos o recargue para intentar de nuevo."
        );
        setErrors((errors) => ({
          ...errors,
          items: response.errorMessage,
        }));
        return;
      }
      rateKey.current = "";

      if (shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL) {
        if (!shipmentDraft.dhlItem?.rate.value) {
          return;
        }

        // Copy the real DHL rate onto the principal shipping line (matched by
        // isItemBase, not a fragile catalog id) and drop the office-delivery
        // line. See applyInternationalRate.
        response.model.items = applyInternationalRate(
          response.model.items,
          shipmentDraft.dhlItem.rate
        );
      }

      if (
        !response.model.items.some((item) => item.isItemBase) ||
        (response.model.items
          .filter((item) => item.isItemBase)
          .some((item) => !item.rate.value) &&
          shipmentDraft.paymentMode !== PaymentMode.CREDIT)
      ) {
        alertService.error(
          "No se ha logrado obtener el cálculo de tarifas.",
          "Por favor espere unos segundos o recargue para intentar de nuevo."
        );

        setErrors((errors) => ({
          ...errors,
          items: response.errorMessage,
        }));
        return;
      }

      setShipmentItems(response.model.items);
      saveDraft({
        items: response.model.items
          .filter(
            (item) =>
              item.mandatory ||
              !!item.couponID ||
              shipmentDraft.items?.some((s) => s.id === item.id)
          )
          .sort((a, b) => a.order - b.order),
        // Stamp the signature of the inputs that produced this quote so a
        // no-change return short-circuits instead of recomputing.
        quoteSignature: currentSignature,
      });
    };

    getAllShipmentItems();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    baseShipmentItems,
    service,
    shipmentDraft.shipper,
    shipmentDraft.buConsignee,
    shipmentDraft.consigneeAddress?.coordinates.lat,
    shipmentDraft.consigneeAddress?.coordinates.lng,
    shipmentDraft.shipperAddress?.coordinates.lat,
    shipmentDraft.shipperAddress?.coordinates.lng,
    shipmentDraft.accountBillTo?.id,
    shipmentDraft.totalChargedWeight,
    saveDraft,
    shipmentDraft.dhlItem,
    // NOTE: buildQuoteSignature is intentionally NOT a dependency. It is a
    // useCallback that closes over the whole shipmentDraft, so listing it would
    // re-fire this effect on every saveDraft (incl. the items:[] guard below),
    // causing an infinite update loop. The signature is computed inside the
    // effect at run time, which reads the current draft via closure.
  ]);

  useEffect(() => {
    if (shipmentDraft.buSource !== user.businessUnit) {
      saveDraft({ buSource: user.businessUnit });
    }
  }, [shipmentDraft.buSource, user.businessUnit, saveDraft]);

  useEffect(() => {
    if (consigneeType === "locker") {
      saveDraft({ service: ShipmentService.NATIONAL_LOCKER });
    } else if (shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL) {
      saveDraft({ service: ShipmentService.STANDARD_INTERNATIONAL });
    } else {
      saveDraft({ service: ShipmentService.STANDARD });
    }
  }, [
    consigneeType,
    shipmentDraft.paymentMode,
    saveDraft,
    shipmentDraft.deliveryType,
  ]);

  useEffect(() => {
    if (openPreShipmentModal) {
      setPreShipmentError("");
      setPreShipmentCode("");
    }
  }, [openPreShipmentModal]);

  useEffect(() => {
    if (!shipmentDraft.items) return;

    const absoluteTotal = shipmentDraft.items
      .filter((item) => !item.rate.isPercentage)
      .reduce((total, item) => total + item.rate.value, 0);
    const percentageTotal = shipmentDraft.items
      .filter((item) => item.rate.isPercentage)
      .reduce((total, item) => total + item.rate.value, 0);

    const total = absoluteTotal * (1 + percentageTotal / 100);

    saveDraft({ total });
  }, [shipmentDraft.items, saveDraft]);

  useEffect(() => {
    if (error?.includes("Remitente inválido")) {
      setClientTypeSelected("shipper");
      setOpenUpdateModal(true);
    } else if (error?.includes("Destinatario inválido")) {
      setClientTypeSelected("consignee");
      setOpenUpdateModal(true);
    }
  }, [error]);

  useEffect(() => {
    const buCode = user.businessUnit?.code;
    if (!buCode) return;

    const getCredit = async () => {
      const response = await getBUCredit(buCode);
      if (!response.didError && response.model) {
        setBuCredit(response.model);
      }
    };

    getCredit();
  }, [user.businessUnit]);

  useEffect(() => {
    const deliveryType = shipmentDraft.deliveryType;
    if (deliveryType === undefined) {
      setDeliveryTypeServices([]);
      return;
    }

    const getDeliveryTypeServices = async () => {
      const services = await getServicesFromDeliveryType(
        deliveryType,
        shipmentDraft.isInternational ?? false
      );

      if (!services.didError && services.model !== null) {
        setDeliveryTypeServices(services.model);
      }
    };

    getDeliveryTypeServices();
  }, [shipmentDraft.deliveryType, shipmentDraft.isInternational]);

  useEffect(() => {
    const accounBillToID = shipmentDraft.accountBillTo?.id;
    const shipperLatitude = shipmentDraft.buSource?.location.coordinates.lat;
    const shipperLongitude = shipmentDraft.buSource?.location.coordinates.lng;
    const consigneeLatitude = shipmentDraft.consigneeAddress?.coordinates.lat;
    const consigneeLongitude = shipmentDraft.consigneeAddress?.coordinates.lng;
    const serviceID = shipmentDraft.service;
    const paymentModeID = shipmentDraft.paymentMode;
    if (
      !accounBillToID ||
      !shipperLatitude ||
      !shipperLongitude ||
      !consigneeLatitude ||
      !consigneeLongitude ||
      !serviceID ||
      !paymentModeID
    ) {
      setTier(undefined);
      setTierBlockedServices([]);
      return;
    }

    const getShipmentTier = async () => {
      const tier = await getTier({
        AccountBillToID: accounBillToID,
        LatitudeShipperAddress: shipperLatitude,
        LongitudeShipperAddress: shipperLongitude,
        LatitudeConsigneeAddress: consigneeLatitude,
        LongitudeConsigneeAddress: consigneeLongitude,
        ServiceID: serviceID,
        PaymentModeID: paymentModeID,
      });

      if (tier.didError || !tier.model) {
        return;
      }

      const blockedServices = await getLockedServicesByTier(tier.model.tierID);

      if (!blockedServices.didError && blockedServices.model) {
        setTierBlockedServices(blockedServices.model);
      } else {
        setTierBlockedServices([]);
      }

      setTier(tier.model);
    };

    getShipmentTier();
  }, [
    shipmentDraft.accountBillTo?.id,
    shipmentDraft.buSource?.location.coordinates.lat,
    shipmentDraft.buSource?.location.coordinates.lng,
    shipmentDraft.consigneeAddress?.coordinates.lat,
    shipmentDraft.consigneeAddress?.coordinates.lng,
    shipmentDraft.service,
    shipmentDraft.paymentMode,
  ]);

  useEffect(() => {
    const paymentMode = shipmentDraft.paymentMode;
    if (paymentMode === undefined) {
      setPaymentModeServices([]);
      return;
    }

    const getPaymentModeServices = async () => {
      const services = await getServicesFromPaymentMode(paymentMode);

      if (!services.didError && services.model !== null) {
        setPaymentModeServices(services.model);
      }
    };

    getPaymentModeServices();
  }, [shipmentDraft.paymentMode]);

  useEffect(() => {
    if (applicationID === undefined) {
      setAppBlockedServices([]);
      return;
    }

    const getLockedServices = async () => {
      const services = await getAppLockedServices(applicationID);

      if (!services.didError && services.model !== null) {
        setAppBlockedServices(services.model);
      }
    };

    getLockedServices();
  }, [applicationID]);

  useEffect(() => {
    const buCode = user.businessUnit?.code;
    if (!buCode) {
      setLockedBUServices([]);
      return;
    }

    const getLockedServices = async () => {
      const services = await getLockBUServices(buCode);

      if (!services.didError && services.model !== null) {
        setLockedBUServices(services.model);
      }
    };

    getLockedServices();
  }, [user.businessUnit?.code]);

  useEffect(() => {
    const accountSiteID = shipmentDraft?.accountSiteID;
    const authorizedServices =
      shipmentDraft.paymentMode === PaymentMode.CREDIT && accountSiteID
        ? shipmentDraft.shipper?.listAuthorizingAccount
            .filter((a) => a.accountSiteID === accountSiteID)
            .map((a) => a.serviceID)
        : undefined;

    const allowedServices = paymentModeServices.filter(
      (service) =>
        deliveryTypeServices.some((s) => s.serviceID === service.serviceID) &&
        !tierBlockedServices.some((s) => s.serviceID === service.serviceID) &&
        (service.receptionInBU ||
          shipmentDraft.deliveryType !== DeliveryType.AT_OFFICE) &&
        !lockedBUServices.some((s) => s.serviceID === service.serviceID) &&
        !appBlockedServices.some((s) => s.serviceID === service.serviceID) &&
        (!authorizedServices || authorizedServices.includes(service.serviceID))
    );
    setAllowedServices(allowedServices);

    if (allowedServices.length === 1) {
      saveDraft({ service: allowedServices[0].serviceID });
    }
  }, [
    saveDraft,
    paymentModeServices,
    deliveryTypeServices,
    tierBlockedServices,
    appBlockedServices,
    shipmentDraft.deliveryType,
    shipmentDraft.shipper,
    shipmentDraft.paymentMode,
    shipmentDraft.accountSiteID,
    lockedBUServices,
  ]);

  useEffect(() => {
    const serviceID = shipmentDraft.service;
    if (serviceID === undefined) {
      return;
    }

    const afunction = async () => {
      const service = await getService(serviceID);

      if (!service.didError && service.model) {
        setService(service.model);
      }
    };

    afunction();
  }, [shipmentDraft.service]);

  useEffect(() => {
    const afunction = async () => {
      const response = await getShipmentEstimatedDeliveryTime(
        undefined,
        undefined,
        shipmentDraft.buSource?.location.locationID,
        shipmentDraft.buConsignee?.location.locationID,
        shipmentDraft.service
      );

      if (response.didError || response.model === null) {
        return;
      }

      setEstimatedDeliveryTime(response.model);
    };

    afunction();
  }, [
    shipmentDraft.buSource,
    shipmentDraft.buConsignee,
    shipmentDraft.service,
  ]);

  return (
    <div className="flex flex-1 flex-col gap-8">
      {/* Shipper and Consignee description */}
      <div>
        <div
          className={classNames(
            "transition-full duration-1000 ease-in-out flex flex-1 items-center pl-2 pr-2 bg-white rounded-md border ",
            active ? "py-6" : "py-2",
            !!error && "border-red-500"
          )}
        >
          <div
            title="Menú de opciones"
            data-te-toggle="tooltip"
            onClick={() => {
              setOpenDropdownMenu((p) => !p);
            }}
            className="rounded-full hover:cursor-pointer p-4"
          >
            <EllipsisVerticalIcon
              className="h-5 w-5 text-gray3-600"
              aria-hidden="true"
            />
          </div>

          <DropdownMenu
            open={openDropdownMenu}
            setOpen={setOpenDropdownMenu}
            onSelect={handleDropdownMenuSelect}
            options={dropdownMenuOptions}
          />

          <div className="flex flex-1 pr-4">
            <ShipmentHeader
              active={active}
              buSource={shipmentDraft.buSource}
              total={shipmentDraft.total}
              shipper={shipmentDraft.shipper}
              isShipperEnterprise={
                !!shipmentDraft.shipper?.agreementID ||
                (shipmentDraft.shipper?.listAuthorizingAccount?.length ?? 0) > 0
              }
              buConsignee={shipmentDraft.buConsignee}
              consignee={shipmentDraft.consignee}
              isConsigneeEnterprise={
                !!shipmentDraft.consignee?.agreementID ||
                (shipmentDraft.consignee?.listAuthorizingAccount?.length ?? 0) >
                  0
              }
              location={
                shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
                  ? shipmentDraft.internationalAddress
                  : shipmentDraft.consigneeAddress
              }
              boxAccount={shipmentDraft.boxAccount}
              shipmentNumber={shipmentDraft.shipmentNumber}
              onClick={onArrowClick}
              isInternational={
                shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
              }
              destination={
                shipmentDraft.dhlDestination &&
                dhlAddressStringBuilder(shipmentDraft.dhlDestination)
              }
            />
          </div>
        </div>
        {hasError &&
          errorLines.map((line, index) => (
            <span key={index} className="text-sm text-red-500 block">
              {line}
            </span>
          ))}
      </div>

      {/* Body */}
      <AnimatedHeight active={active} duration={1000}>
        <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 py-6 gap-6">
          <div className="flex flex-1 2xl:flex-row flex-col gap-4">
            {/* Search shipper */}
            <div className="relative flex flex-1" ref={shipperRef}>
              <AccountSearch
                title="Remitente"
                selected={shipmentDraft.shipper}
                error={errors.shipper}
                disabled={!shipperIsEditable}
                placeholder="Buscar remitente..."
                ignoredAccounts={[
                  { abreviationName: "J-", identificationNumber: "306842675" },
                ]}
                onSelectClient={async (shipper) => {
                  const validations = await validateAccount(shipper, countries);

                  saveDraft({
                    shipper,
                    buConsignee: undefined,
                    consigneeAddress: undefined,
                    boxAccount: undefined,
                    paymentMode: undefined,
                    deliveryType: DeliveryType.AT_OFFICE,
                    //preShipmentHeaderID: undefined,
                  });
                  setErrors((errors) => ({ ...errors, shipper: "" }));

                  if (!!shipper && validations.error) {
                    setClientTypeSelected("shipper");
                    setOpenUpdateModal(true);
                  }
                }}
                openCreationModal={() => {
                  setOpenCreationModal(true);
                }}
              />

              {shipmentDrafts.length === 1 &&
                !shipmentDraft.shipmentNumber &&
                !shipmentDraft.id &&
                (!shipmentDraft.status ||
                  shipmentDraft.status === ShipmentStatus.DRAFT) &&
                !shipmentDraft.document &&
                !shipmentCreateDocument && (
                  <LinkText
                    text="Cargar Pre-Guía"
                    className="absolute top-0 right-0 text-sm"
                    onClick={() => setOpenPreShipmentModal(true)}
                  />
                )}
            </div>

            {/* Search consignee */}
            <div className="flex flex-1 flex-col" ref={consigneeRef}>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-m font-medium  text-gray-900 truncate">
                  Destinatario
                </label>

                <FormSimpleRadioGroup
                  horizontal
                  name={`${randomId}-consigneeType`}
                  disabled={!shipmentDraft.shipper || !shipperIsEditable}
                  selected={consigneeType}
                  onSelectOption={(option) => {
                    if (
                      shipmentDraft.service === ShipmentService.DOCUMENT &&
                      shipmentDraft.pieces!.length > 0
                    ) {
                      setAuxConsigneeType(option);
                      setOpenConsigneeTypeModal(true);
                      return;
                    }

                    saveDraft({
                      buConsignee: undefined,
                      consignee: undefined,
                      consigneeAddress: undefined,
                      boxAccount: undefined,
                      deliveryType: DeliveryType.AT_OFFICE,
                    });
                    setConsigneeType(option);
                  }}
                  options={[
                    { value: "account", name: "Persona" },
                    { value: "locker", name: "Casillero" },
                  ]}
                />
              </div>

              <div className="flex flex-1">
                {consigneeType === "locker" ? (
                  <BoxAccountSearch
                    selected={shipmentDraft.boxAccount}
                    shipperAccount={shipmentDraft.shipper}
                    error={errors.consignee}
                    setBoxAccount={async (
                      boxAccount,
                      consignee,
                      businessUnit
                    ) => {
                      const validations = await validateAccount(
                        consignee,
                        countries
                      );

                      saveDraft({
                        boxAccount,
                        accountBillTo: consignee,
                        accountSiteID: undefined,
                        consignee,
                        buConsignee: businessUnit,
                        consigneeAddress: businessUnit.location,
                        deliveryType: DeliveryType.AT_OFFICE,
                        paymentMode: PaymentMode.CREDIT,
                      });
                      setErrors((errors) => ({ ...errors, consignee: "" }));

                      if (!!consignee && validations.error) {
                        setClientTypeSelected("consignee");
                        setOpenUpdateModal(true);
                      }
                    }}
                  />
                ) : whitelist.length > 0 ? (
                  <div className="flex flex-1 flex-col">
                    <div className="relative w-full" id={`input-whitelist`}>
                      <FormGroupSearch
                        label=""
                        error={errors.consignee}
                        value={whitelistSearch}
                        disabled={!shipmentDraft.shipper}
                        placeholder="Buscar destinatario permitido..."
                        optionGroups={[
                          {
                            title: "Destinatarios Permitidos",
                            options: whitelist,
                          },
                        ]}
                        onChange={(event) =>
                          setWhitelistSearch(event.target.value)
                        }
                        filter={(option, value) =>
                          filterDestination(
                            value,
                            option.client,
                            option.location
                          )
                        }
                        onChangeFocus={(focus) => {
                          if (!focus && shipmentDraft.consignee) {
                            setWhitelistSearch(
                              formatName(
                                shipmentDraft.consignee.accountFullName
                              )
                            );
                          }
                        }}
                        onSelectOption={async (destination) => {
                          const consignee =
                            (await getAccount(destination.client.id)) ??
                            undefined;

                          const validations = await validateAccount(
                            consignee,
                            countries
                          );

                          saveDraft({
                            buConsignee: destination.businessUnit,
                            consignee: consignee,
                            consigneeAddress: destination.location,
                            deliveryType: destination.homeDelivery
                              ? DeliveryType.AT_HOME
                              : DeliveryType.AT_OFFICE,
                          });
                          setErrors((errors) => ({
                            ...errors,
                            consignee: "",
                            location: "",
                          }));

                          if (validations.error) {
                            setClientTypeSelected("consignee");
                            setOpenUpdateModal(true);
                          }
                        }}
                        RenderOption={({ option }) => (
                          <WhitelistOption {...option} />
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  <AccountSearchFavoriteHistoric
                    title=""
                    shipperId={shipmentDraft.shipper?.id}
                    selected={shipmentDraft.consignee}
                    disabled={!shipmentDraft.shipper}
                    error={errors.consignee}
                    placeholder="Buscar destinatario..."
                    onSelectClient={async (client) => {
                      const consignee =
                        (await getAccount(client.id)) ?? undefined;
                      const validations = await validateAccount(
                        consignee,
                        countries
                      );

                      saveDraft({ consignee });
                      setErrors((errors) => ({ ...errors, consignee: "" }));

                      if (!!consignee && validations.error) {
                        setClientTypeSelected("consignee");
                        setOpenUpdateModal(true);
                      }
                    }}
                    onSelectDestination={async (destination) => {
                      let businessUnit: BusinessUnitInterface;
                      const deliveryType = destination.deliveryTypeID;

                      const consignee = await getAccount(
                        destination.consigneeAccountID
                      );
                      if (!consignee) {
                        alertService.error("No se consiguió el cliente");
                        return;
                      }

                      if (deliveryType === DeliveryType.AT_OFFICE) {
                        businessUnit = businessUnits.find(
                          (bu) => bu.code === destination.buCode
                        )!;
                      } else {
                        businessUnit = await getBusinessUnitByTerritory(
                          destination.longitude,
                          destination.latitude
                        ).then((response) => response.model!);
                      }

                      saveDraft({
                        buConsignee: businessUnit,
                        consignee: consignee,
                        consigneeAddress: {
                          name: destination.addressName,
                          address: destination.addressLine1,
                          reference: destination.addressLandMark,
                          postalCode: destination.postalCode,
                          coordinates: {
                            lat: destination.latitude,
                            lng: destination.longitude,
                          },
                        },
                        deliveryType,
                      });
                      setErrors((errors) => ({ ...errors, location: "" }));
                    }}
                    openCreationModal={() => {
                      setClientTypeSelected("consignee");
                      setOpenCreationModal(true);
                    }}
                    autoCompleteOff={true}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Search destination */}
          <div
            className={classNames(
              !!shipmentDraft.shipper &&
                !!shipmentDraft.consignee &&
                !shipmentDraft.boxAccount &&
                whitelist.length === 0
                ? "flex"
                : "hidden"
            )}
          >
            <div className="flex flex-1 2xl:flex-row flex-col gap-4">
              <div className="flex flex-1 flex-col" ref={locationRef}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <label className="block text-m font-medium  text-gray-900 truncate">
                      Destino
                    </label>

                    <LinkText
                      text="Cómo llenar"
                      className={classNames(
                        "text-xs",
                        shipmentDraft.deliveryType !== DeliveryType.AT_HOME &&
                          "hidden"
                      )}
                      onClick={() => setOpenHomeDeliveryHelp(true)}
                    />

                    <LinkText
                      text="Cómo llenar"
                      className={classNames(
                        "text-xs",
                        shipmentDraft.deliveryType !==
                          DeliveryType.INTERNATIONAL && "hidden"
                      )}
                      onClick={() => setOpenInternationalDeliveryHelp(true)}
                    />
                  </div>

                  <FormSimpleRadioGroup
                    horizontal
                    name={`${randomId}-locationType`}
                    disabled={
                      !shipmentDraft.shipper ||
                      !!shipmentDraft.boxAccount ||
                      !shipmentDraft.consignee
                    }
                    selected={shipmentDraft.deliveryType}
                    onSelectOption={(option) => {
                      setInternational(option === DeliveryType.INTERNATIONAL);
                      saveDraft({
                        consigneeAddress: undefined,
                        buConsignee: undefined,
                        deliveryType: option,
                        paymentMode: PaymentMode.CONTADO,
                        isInternational: option === DeliveryType.INTERNATIONAL,
                        internationalAddress: undefined,
                        cityValue: "",
                        dhlDestination: undefined,
                      });
                      handlePaymentModeChange(PaymentMode.CONTADO);
                    }}
                    options={[
                      { value: DeliveryType.AT_OFFICE, name: "Tienda" },
                      { value: DeliveryType.AT_HOME, name: "Domicilio" },
                      {
                        value: DeliveryType.INTERNATIONAL,
                        name: "Internacional",
                      },
                    ]}
                  />
                </div>

                <div className="flex">
                  {shipmentDraft.deliveryType === DeliveryType.AT_OFFICE && (
                    <BusinessUnitSearch
                      businessUnitsOptions={destinationBusinessUnits}
                      includeLockedBusinessUnits={true}
                      selected={shipmentDraft.buConsignee}
                      error={errors.location}
                      disabled={
                        !shipmentDraft.shipper ||
                        !!shipmentDraft.boxAccount ||
                        !shipmentDraft.consignee
                      }
                      setSelectedLocation={(businessUnit) => {
                        saveDraft({
                          buConsignee: businessUnit,
                          consigneeAddress: businessUnit.location,
                        });
                      }}
                    />
                  )}

                  {shipmentDraft.deliveryType === DeliveryType.AT_HOME && (
                    <AddressSearch
                      businessUnits={businessUnits}
                      selected={shipmentDraft.consigneeAddress}
                      region={AutocompleteRegion.TEALCA}
                      error={errors.location}
                      isInternational={false}
                      disabled={
                        !shipmentDraft.shipper || !!shipmentDraft.boxAccount
                      }
                      setSelectedLocation={(location, buConsignee) => {
                        if (!auxReference) {
                          saveDraft({
                            buConsignee,
                            consigneeAddress: location,
                          });
                        } else {
                          saveDraft({
                            buConsignee,
                            consigneeAddress: {
                              ...location,
                              reference: auxReference,
                            },
                          });
                          setAuxReference("");
                        }
                      }}
                    />
                  )}

                  {shipmentDraft.deliveryType ===
                    DeliveryType.INTERNATIONAL && (
                    <>
                      <AddressSearch
                        businessUnits={businessUnits}
                        selected={shipmentDraft.internationalAddress}
                        region={AutocompleteRegion.INTERNATIONAL}
                        isInternational={true}
                        error={errors.location}
                        disabled={
                          !shipmentDraft.shipper || !!shipmentDraft.boxAccount
                        }
                        setSelectedLocation={(location) => {
                          const buConsignee = businessUnits.find(
                            (b) => b.code === "EXPNN"
                          );
                          saveDraft({
                            buConsignee,
                            consigneeAddress: location,
                            internationalLocation: {
                              ...shipmentDraft.internationalLocation,
                              country: location.name,
                            },
                            internationalAddress: location,
                            cityValue: "",
                            cityName: "",
                            dhlDestination: undefined,
                          });
                          setCountryName(location.address);
                          setCityOptions(undefined);
                        }}
                        setShortCountryName={(countryCode) =>
                          saveDraft({ countryCode })
                        }
                      />
                      <FormSearch
                        value={shipmentDraft.cityValue ?? ""}
                        onChange={handleCityChange}
                        isInternational={true}
                        loader={loader}
                        title="Busque la ciudad o localidad en su idioma original. Ejemplo: New York. Seleccione la opción que corresponda con el código postal."
                        data-te-toggle="tooltip"
                        placeholder="Buscar ciudad..."
                        options={(cityOptions && cityOptions.address) || []}
                        isRequiredLabel={false}
                        RenderOption={({ option }) => (
                          <div>{dhlAddressStringBuilder(option)}</div>
                        )}
                        onSelectOption={(option) => {
                          setCityOptions(undefined);
                          saveDraft({
                            internationalLocation: {
                              ...shipmentDraft.internationalLocation,
                              city:
                                option.cityName[0] +
                                option.cityName.slice(1).toLocaleLowerCase(),
                              postalCode: option.postalCode,
                            },
                            cityName: option.cityName,
                            cityValue: dhlAddressStringBuilder(option),
                            dhlDestination: option,
                          });
                          updateInputValue();
                        }}
                      />
                    </>
                  )}
                </div>
              </div>

              <div
                className={classNames(
                  "flex flex-1 flex-col",
                  shipmentDraft.deliveryType === DeliveryType.AT_OFFICE &&
                    "hidden"
                )}
              >
                <div className="flex items-center gap-2">
                  <label className="block text-m font-medium text-gray-900 truncate">
                    {shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
                      ? "Dirección"
                      : "Punto de Referencia"}
                  </label>

                  <LinkText
                    text="Cómo llenar"
                    className={classNames(
                      "text-xs",
                      shipmentDraft.deliveryType !==
                        DeliveryType.INTERNATIONAL && "hidden"
                    )}
                    onClick={() => setOpenInternationalAddressHelp(true)}
                  />
                </div>

                <FormTextInput
                  value={
                    shipmentDraft.consigneeAddress?.reference ?? auxReference
                  }
                  title="Complete la dirección para el envío con el domicilio, calle, municipio/parroquia/urbanización."
                  data-te-toggle="tooltip"
                  onChange={handleRefChange}
                  name="reference"
                  label=""
                  disabled={!shipmentDraft.consigneeAddress}
                  isRequired={true}
                  maxLength={250}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Select pay modality */}
          <div
            className={classNames(
              (!!shipmentDraft.shipper &&
                !!shipmentDraft.consigneeAddress &&
                !shipmentDraft.boxAccount &&
                shipmentDraft.shipperAuth ===
                  AccountAuthorization.NO_AUTHORIZED) ||
                shipmentDraft.shipperAuth !== AccountAuthorization.NO_AUTHORIZED
                ? "flex"
                : "hidden"
            )}
          >
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-1 flex-col gap-4">
                {shipmentDraft.shipperAuth ===
                  AccountAuthorization.NO_AUTHORIZED  &&
                  (!!shipmentDraft.boxAccount ||
                    !!shipmentDraft.consigneeAddress) && (
                    selectedBusinessUnitIsDefault ? (
                      <FormSimpleRadioGroup
                        horizontal
                        name={`${randomId}-paymentMode`}
                        selected={shipmentDraft.paymentMode}
                        options={paymentModes}
                        label="Modalidad de pago"
                        onSelectOption={(paymentMode) => {
                          if (
                            paymentMode !== PaymentMode.CONTADO &&
                            shipmentDraft.service === ShipmentService.DOCUMENT &&
                            shipmentDraft.pieces!.length > 0
                          ) {
                            setAuxPaymentMode(paymentMode);
                            setOpenPaymentModeModal(true);
                            return;
                          }

                          handlePaymentModeChange(paymentMode);
                        }}
                        disabled={
                          disablePaymentMode ||
                          !shipmentDraft.shipper ||
                          !shipmentDraft.consigneeAddress ||
                          !!shipmentDraft.boxAccount ||
                          //!shipperIsEditable ||
                          shipmentDraft.deliveryType ===
                            DeliveryType.INTERNATIONAL ||
                          !!shipmentDraft.preShipmentHeaderID
                        }
                      />
                    ) : (
                      <FormSimpleRadioGroup<number | string>
                        label="Modalidad de pago"
                        horizontal
                        name={`${randomId}-paymentMode-with-authAccount`}
                        selected={
                          shipmentDraft.accountBillTo &&
                          shipmentDraft.paymentMode === PaymentMode.CREDIT
                            ? `${shipmentDraft.accountBillTo.id}-${shipmentDraft.accountSiteID}`
                            : shipmentDraft.paymentMode
                        }
                        options={paymentModesWithAuth.map((option) => {
                          const value = option.value;
                          if (typeof value !== "object") {
                            return {
                              name: option.name,
                              value: value,
                              disabled: option.disabled,
                              tooltip: option.tooltip,
                            };
                          } else {
                            return {
                              name: option.name,
                              value: `${value.id}-${value.accountSiteID}`,
                              disabled: option.disabled,
                              tooltip: option.tooltip,
                            };
                          }
                        })}
                        disabled={
                          disablePaymentMode ||
                          !!shipmentDraft.preShipmentHeaderID
                        }
                        onSelectOption={(option) => {
                          if (
                            option !== PaymentMode.CONTADO &&
                            shipmentDraft.service ===
                              ShipmentService.DOCUMENT &&
                            shipmentDraft.pieces!.length > 0
                          ) {
                            setAuxPaymentMode(option);
                            setOpenPaymentModeModal(true);
                            return;
                          }

                          handlePaymentModeChange(option);
                        }}
                      />
                    )
                  )}
                {(shipmentDraft.shipperAuth ===
                  AccountAuthorization.AUTHORIZED ||
                  shipmentDraft.shipperAuth ===
                    AccountAuthorization.ENTERPRISE) &&
                  !shipmentDraft.boxAccount &&
                  !!shipmentDraft.consigneeAddress &&
                  shipmentDraft.deliveryType !== DeliveryType.INTERNATIONAL && (
                    <>
                      {paymentModesWithAuth.length <= 3 ? (
                        <FormSimpleRadioGroup<number | string>
                          label="Modalidad de pago"
                          horizontal
                          name={`${randomId}-paymentMode-with-authAccount`}
                          selected={
                            shipmentDraft.accountBillTo &&
                            shipmentDraft.paymentMode === PaymentMode.CREDIT
                              ? `${shipmentDraft.accountBillTo.id}-${shipmentDraft.accountSiteID}`
                              : shipmentDraft.paymentMode
                          }
                          options={paymentModesWithAuth.map((option) => {
                            const value = option.value;
                            if (typeof value !== "object") {
                              return {
                                name: option.name,
                                value: value,
                                disabled: option.disabled,
                                tooltip: option.tooltip,
                              };
                            } else {
                              return {
                                name: option.name,
                                value: `${value.id}-${value.accountSiteID}`,
                                disabled: option.disabled,
                                tooltip: option.tooltip,
                              };
                            }
                          })}
                          disabled={
                            disablePaymentMode ||
                            !!shipmentDraft.preShipmentHeaderID
                          }
                          onSelectOption={(option) => {
                            if (
                              option !== PaymentMode.CONTADO &&
                              shipmentDraft.service ===
                                ShipmentService.DOCUMENT &&
                              shipmentDraft.pieces!.length > 0
                            ) {
                              setAuxPaymentMode(option);
                              setOpenPaymentModeModal(true);
                              return;
                            }

                            handlePaymentModeChange(option);
                          }}
                        />
                      ) : (
                        <FormSelectOptionObjects
                          label="Modalidad de pago"
                          selected={
                            shipmentDraft.accountBillTo &&
                            shipmentDraft.paymentMode === PaymentMode.CREDIT
                              ? `${shipmentDraft.accountBillTo.id}-${shipmentDraft.accountSiteID}`
                              : shipmentDraft.paymentMode
                          }
                          name={`${randomId}-paymentMode-with-authAccount`}
                          options={paymentModesWithAuth.map((option) => {
                            const value = option.value;
                            if (typeof value !== "object") {
                              return {
                                name: option.name,
                                value: value,
                                disabled: option.disabled,
                                tooltip: option.tooltip,
                              };
                            } else {
                              return {
                                name: option.name,
                                value: `${value.id}-${value.accountSiteID}`,
                              };
                            }
                          })}
                          disabled={
                            disablePaymentMode ||
                            !!shipmentDraft.preShipmentHeaderID
                          }
                          onSelectOption={(option) => {
                            if (
                              option !== PaymentMode.CONTADO &&
                              shipmentDraft.service ===
                                ShipmentService.DOCUMENT &&
                              shipmentDraft.pieces!.length > 0
                            ) {
                              setAuxPaymentMode(option);
                              setOpenPaymentModeModal(true);
                              return;
                            }

                            handlePaymentModeChange(option);
                          }}
                          /*optionString={(selected) =>
                            paymentModesWithAuth.find(
                              (payMode) => payMode.value === selected
                            )?.name ?? ""
                          }*/
                          //optionString={(option) => option.name}
                          onSearch={(option, search) => {
                            return option.name
                              .toLowerCase()
                              .split(" ")
                              .some((s) => s.startsWith(search));
                          }}
                        />
                      )}
                    </>
                  )}
              </div>

              <div
                className={classNames(
                  shipmentDraft.paymentMode !== PaymentMode.BOXOFFICE_CREDIT &&
                    "hidden"
                )}
              >
                <p className="text-xs text-gray-600">
                  Crédito Taquilla disponible:{" "}
                  <span className="font-semibold">
                    {currencyExchangeText(
                      buCredit?.balance ?? 0,
                      exchanges,
                      "USD",
                      buCredit?.currencyID === 1 ? "BS" : "USD"
                    )}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <hr className="border-gray-300 my-6" />

          {/* Pieces */}
          <div className="flex flex-1 flex-col gap-8" ref={piecesRef}>
            <ShipmentPiecesDetails
              id={randomId}
              service={shipmentDraft.service ?? ShipmentService.STANDARD}
              typeDisabled={consigneeType === "locker"}
              pieces={shipmentDraft.pieces ?? []}
              pieceCategories={pieceCategories}
              paymentMode={shipmentDraft.paymentMode}
              error={errors.pieces}
              setServiceType={(type) => saveDraft({ service: type })}
              serviceOptions={availableServiceTypes}
              showDimentionsHelp={
                shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
              }
              setOpenDimentionsHelp={setOpenInternationalDimentionsHelp}
              isInternational={
                shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
              }
              chargedWeight={shipmentDraft.totalChargedWeight}
              physicalWeight={shipmentDraft.totalPhysicalWeight}
              dimensionalWeight={shipmentDraft.totalDimensionalWeight}
              volumetricFactor={shipmentDraft.dimensionalFactor}
              maxSumWeight={Math.min(
                piecesRestrictions.maxSumWeight ?? Infinity,
                service?.maxWeight ?? Infinity
              )}
              minSumWeight={service?.minWeight}
              applyMinValue={
                !shipmentDraft.boxAccount &&
                shipmentDraft.service === ShipmentService.STANDARD &&
                (shipmentDraft.paymentMode === PaymentMode.COD ||
                  shipmentDraft.paymentMode === PaymentMode.CONTADO)
              }
              cleaned={cleaned}
              setCleaned={setCleaned}
              setPieces={(pieces) => {
                const value =
                  parseFloat(
                    shipmentDraft.declaredValue
                      ? shipmentDraft.declaredValue
                      : "0"
                  ) / pieces.length;

                saveDraft({
                  pieces: pieces!.map((piece) => ({
                    ...piece,
                    value,
                  })),
                });
                setErrors((errors) => ({ ...errors, pieces: false }));
              }}
              autoCompleteOff={true}
            />

            <div className="flex gap-4">
              <div className="w-full">
                <FormTextInput
                  value={declaredValue}
                  error={errors.declaredValue}
                  onChange={(event) => {
                    if (handleNumberChange(event)) {
                      if (
                        !(
                          shipmentDraft.deliveryType ===
                          DeliveryType.INTERNATIONAL
                        )
                      ) {
                        verifyDeclaredValue(event.target.value);
                      }
                      setDeclaredValue((state) => event.target.value);
                    }
                  }}
                  onClick={handleDeclaredValueChange}
                  name="declaredValue"
                  label="Valor Declarado en Dólares"
                  autoComplete="off"
                />
              </div>

              <div>
                <PrimaryButton
                  className="w-32 mt-8"
                  disabled={
                    declaredValue === shipmentDraft.declaredValue || loading
                  }
                  onClick={handleDeclaredValueChange}
                >
                  Aplicar
                </PrimaryButton>
              </div>
            </div>
          </div>

          <hr className="border-gray-300 my-6" />

          {/* Observations */}
          <div className="flex flex-1 flex-col">
            <FormTextAreaInput
              rows={4}
              maxLength={192}
              value={shipmentDraft.observations ?? ""}
              onChange={(event) => {
                saveDraft({
                  observations: event.target.value,
                });
              }}
              name="observations"
              label="Observaciones"
              className="resize-none"
            />
          </div>

          <hr className="border-gray-300 my-6" />

          {/* Items */}
          <div className="relative">
            <div
              className={classNames(
                "absolute top-0 right-2",
                isLoading === 0 && "hidden"
              )}
            >
              <LoadingIcon size="1.5rem" />
            </div>

            <ShipmentItemTable
              editable
              showCouponBadge
              total={shipmentDraft.total}
              items={shipmentDraft.items ?? []}
              deliveryDistance={shipmentDraft.deliveryDistance}
              pickupDistance={shipmentDraft.pickupDistance}
              declaredValue={+(shipmentDraft.declaredValue ?? "0")}
              availableItems={shipmentItems.filter(
                (item) =>
                  !item.mandatory &&
                  !(shipmentDraft.items ?? []).some((i) => i.id === item.id)
              )}
              isInternational={
                shipmentDraft.deliveryType === DeliveryType.INTERNATIONAL
              }
              setItems={(items) => {
                saveDraft({
                  items: items.sort((a, b) => a.order - b.order),
                  // The user added/removed an optional service: the shipment
                  // changed, so invalidate the cached quote to force one honest
                  // recompute (which re-stamps the signature) on next mount.
                  quoteSignature: undefined,
                });
              }}
              exchanges={exchanges}
            />

            {errors.items && (
              <p className="text-red-600 mt-2 ml-2">{errors.items}</p>
            )}

            <p
              className={classNames(
                "text-right text-sm mt-4 text-gray-700",
                !estimatedDeliveryDate && "hidden"
              )}
            >
              Fecha de entrega estimada:{" "}
              <span className="font-semibold">{estimatedDeliveryDate}</span>
            </p>
          </div>
        </div>
      </AnimatedHeight>

      {/* Delete modal */}
      <Modal openModal={openDeleteModal} setOpenModal={setOpenDeleteModal}>
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold leading-7 text-gray-900">
            Eliminar guía
          </h2>

          <div className="flex flex-row gap-1">
            <p className="text-gray-500">
              ¿Está seguro que desea eliminar la guía?
            </p>
          </div>

          <div className="flex flex-row gap-4 items-center justify-between">
            <SecondaryButton
              onClick={() => {
                setOpenDeleteModal(false);
                deleteDraft();
              }}
              className="px-4 py-2"
            >
              Eliminar
            </SecondaryButton>
            <PrimaryButton onClick={() => setOpenDeleteModal(false)}>
              Cancelar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {/* Clean modal */}
      <Modal openModal={openCleanModal} setOpenModal={setOpenCleanModal}>
        <div className="flex flex-col gap-4 max-w-[20rem]">
          <h2 className="text-2xl font-semibold leading-7 text-gray-900 text-center">
            Limpiar guía
          </h2>

          <div>
            <p className="text-gray-500 text-center text-pretty">
              ¿Está seguro que desea limpiar la guía?
            </p>
            <p className="text-gray-500 text-center text-sm">
              Esta acción borrará todos los campos ingresados y no se puede
              deshacer.
            </p>
          </div>

          <div className="flex flex-row gap-4 items-center justify-between">
            <SecondaryButton onClick={() => setOpenCleanModal(false)}>
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                setOpenCleanModal(false);
                clean();
              }}
              className="px-4 py-2"
            >
              Limpiar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal openModal={openCancelModal} setOpenModal={setOpenCancelModal}>
        <div className="flex flex-col gap-4 max-w-[25rem] items-center">
          <h2 className="text-2xl font-semibold leading-7 text-gray-900">
            Anular guía{" "}
            <span className="text-indigo-600">
              {shipmentDraft?.shipmentNumber}
            </span>
          </h2>

          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32 text-indigo-600" />
          </div>

          <div className="text-center">
            <p className="mt-2 text-center text-gray-900">
              ¿Estás seguro que quieres anular la guía{" "}
              <span className="font-bold">{shipmentDraft?.shipmentNumber}</span>
              ?
            </p>
            <p>Esta acción es irreversible</p>
          </div>

          <div className="mt-4 flex w-full flex-row justify-between gap-4">
            <SecondaryButton
              className="!w-32"
              onClick={() => setOpenCancelModal(false)}
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton className="!w-32" onClick={handleAnulation}>
              Anular
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {/* Creation modal */}
      <CreateClientModal
        openModal={openCreationModal}
        setOpenModal={setOpenCreationModal}
        setSelectedItem={(client) => {
          if (clientTypeSelected === "shipper") {
            saveDraft({
              shipper: client,
              consignee: undefined,
              consigneeAddress: undefined,
              boxAccount: undefined,
              buConsignee: undefined,
              deliveryType: DeliveryType.AT_OFFICE,
            });
          } else {
            saveDraft({ consignee: client });
          }
        }}
      />

      {/* Update modal */}
      {openUpdateModal && (
        <UpdateClientModal
          openModal={openUpdateModal}
          setOpenModal={setOpenUpdateModal}
          setSelectedItem={(client) => {
            if (clientTypeSelected === "shipper") {
              saveDraft({ shipper: client });
            } else {
              saveDraft({ consignee: client });
            }
          }}
          selectedItem={
            clientTypeSelected === "shipper"
              ? shipmentDraft.shipper!
              : shipmentDraft.consignee!!
          }
        />
      )}

      {/* Load pre-shipment modal */}
      <Modal
        openModal={openPreShipmentModal}
        setOpenModal={setOpenPreShipmentModal}
      >
        <div className="flex flex-col gap-4" style={{ minWidth: "20rem" }}>
          <h2 className="text-2xl font-semibold leading-7 text-gray-900">
            Cargar Pre-Guia
          </h2>

          <div>
            <FormTextInput
              type="number"
              value={preShipmentCode}
              error={preShipmentError}
              onChange={(event) => {
                setPreShipmentError("");
                setPreShipmentCode(event.target.value);
              }}
              name="pre-shipment-code"
              label="Código de Pre-Guía"
              placeholder="Ejemplo: 0000"
            />
          </div>

          <div className="flex flex-row gap-4 items-center justify-between">
            <SecondaryButton
              onClick={() => setOpenPreShipmentModal(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton type="submit" onClick={handlePreShipmentSubmit}>
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openConsigneeTypeModal}
        setOpenModal={setOpenConsigneeTypeModal}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "20rem" }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32" />
          </div>

          <p className="mt-2 text-center text-gray-900">
            Esta acción eliminará las piezas seleccionadas. ¿Desea continuar?
          </p>

          <div className="mt-4 flex w-full flex-row justify-between gap-4">
            <SecondaryButton
              className="px-4"
              onClick={() => setOpenConsigneeTypeModal(false)}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              className="px-4"
              onClick={() => {
                saveDraft({
                  pieces: [],
                  buConsignee: undefined,
                  consignee: undefined,
                  consigneeAddress: undefined,
                  boxAccount: undefined,
                  deliveryType: DeliveryType.AT_OFFICE,
                });
                setConsigneeType(auxConsigneeType);
                setOpenConsigneeTypeModal(false);
              }}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openPaymentModeModal}
        setOpenModal={setOpenPaymentModeModal}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "20rem" }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32" />
          </div>

          <p className="mt-2 text-center text-gray-900">
            Esta acción eliminará las piezas seleccionadas. ¿Desea continuar?
          </p>

          <div className="mt-4 flex w-full flex-row justify-between gap-4">
            <SecondaryButton
              className="px-4"
              onClick={() => setOpenPaymentModeModal(false)}
            >
              Cancelar
            </SecondaryButton>
            <PrimaryButton
              className="px-4"
              onClick={async () => {
                saveDraft({ pieces: [] });
                await handlePaymentModeChange(auxPaymentMode);
                setOpenPaymentModeModal(false);
              }}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {/* Help modals */}
      <Modal
        openModal={openHomeDeliveryHelp}
        setOpenModal={setOpenHomeDeliveryHelp}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "30rem" }}
        >
          <p className="mt-2 text-gray-900 text-sm text-balance">
            Asegúrese de escribir una dirección completa que incluya calle y
            domicilio (edificio, intersección, nombre del comercio, sector,
            nombre de la quinta, etc.) para que el punto que Google Maps escoja
            sea preciso y dentro del polígono correspondiente de la tienda. Por
            ejemplo: "Av. Francisco de Miranda, Caracas" es una dirección
            incompleta (aunque el sistema la acepta) ya que no permite entender
            a qué altura de la misma se debe hacer la entrega a domicilio y en
            consecuencia se asigna arbitrariamente a alguna tienda (que tiene
            altas probabilidades de no ser la correcta).
          </p>

          <div className="mt-6 flex w-full flex-row justify-center gap-4">
            <PrimaryButton
              className="!px-10"
              onClick={() => setOpenHomeDeliveryHelp(false)}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openInternationalDeliveryHelp}
        setOpenModal={setOpenInternationalDeliveryHelp}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "30rem" }}
        >
          <p className="mt-2 text-gray-900 text-sm text-balance">
            Busque la ciudad o localidad en su idioma original. Ejemplo:{" "}
            <span className="italic">New York</span>
            <br /> <br />
            Seleccione la opción que corresponda con el código postal.
            <br />
            <br />
            Sólo busque por localidad o código postal, no ambos a la vez ya que
            no arroja resultados.
            <br />
            <br />
            En ciertos países, por ejemplo Chile, la búsqueda también retorna
            resultados por suburbios (o barrios).
            <br />
            <br />
            Si una localidad o código postal no aparece, pídale al cliente una
            dirección alternativa ya que DHL no entrega en ese destino.
          </p>

          <div className="mt-6 flex w-full flex-row justify-center gap-4">
            <PrimaryButton
              className="!px-10"
              onClick={() => setOpenInternationalDeliveryHelp(false)}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        openModal={openInternationalAddressHelp}
        setOpenModal={setOpenInternationalAddressHelp}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "35rem" }}
        >
          <p className="mt-2 text-gray-900 text-sm text-balance">
            Complete la dirección para el envío con el domicilio, calle,
            municipio/urbanización/barrio.
          </p>

          <div className="mt-6 flex w-full flex-row justify-center gap-4">
            <PrimaryButton
              className="!px-10"
              onClick={() => setOpenInternationalAddressHelp(false)}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {/* <Modal
        openModal={openInternationalDimentionsHelp}
        setOpenModal={setOpenInternationalDimentionsHelp}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "30rem" }}
        >
          <p className="mt-2 text-gray-900 text-sm text-balance">
            Asegúrese de llenar cada dimensión como corresponde ya que DHL puede
            cotizar precios distintos según el orden de llenado. Ejemplo: No es
            la misma tarifa una pieza de 20 de alto x 20 de ancho x 20 de largo
            que 20 de alto x 20 de ancho x 10 de largo.
          </p>

          <div className="mt-6 flex w-full flex-row justify-center gap-4">
            <PrimaryButton
              className="!px-10"
              onClick={() => setOpenInternationalDimentionsHelp(false)}
            >
              Aceptar
            </PrimaryButton>
          </div>
        </div>
      </Modal> */}

      <GoogleMap
        id={`dummy-map-${randomId}`}
        mapContainerStyle={{ height: "0", width: "0" }}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      />
    </div>
  );
};

export default ShipmentForm;
